import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import client from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import TimerIcon from '@mui/icons-material/Timer';
import VerifiedIcon from '@mui/icons-material/Verified';
import CollapsibleModuleHeaderCard from '../../components/CollapsibleModuleHeaderCard';
import DateInputField from '../../components/DateInputField';
import { exportAssessmentPdf, updateAssessmentStationTimerSession } from '../../api/assessments';
import {
  buildAssessmentPersistentTimer,
  getElapsedMsFromStartReference,
  readAssessmentPersistentTimer,
} from '../../utils/persistentTimerState';
import { downloadPdfResponse, extractPdfErrorMessage } from '../../utils/pdfDownload';
import { readPersistedTab, writePersistedTab } from '../../utils/tabPersistence';
import {
  canEditAssessments,
  hasAssessmentCandidateRole,
  isAssessmentObserver,
  canManageAssessmentEvents,
  canViewAssessmentLiveResults,
} from '../../config/roleAccess';
import { redownloadDienstzeitProtokollPdf } from '../../components/pdf/dienstzeitPdfExport';

const ASSESSMENT_OFFLINE_MODE_ENABLED = false;
const ASSESSMENT_HEADER_COLLAPSE_STORAGE_KEY = 'assessment.header.expanded.v1';
const ASSESSMENT_ACTIVE_TAB_STORAGE_KEY = 'assessment.active-tab.v1';
const ASSESSMENT_TAB_BOARD = 'board';
const ASSESSMENT_TAB_LIVE = 'live';
const ASSESSMENT_TAB_FINAL = 'final';
const ASSESSMENT_TAB_STATIONS = 'stations';
const ASSESSMENT_TAB_MINE = 'mine';
const ASSESSMENT_TAB_MANAGE = 'manage';
const ASSESSMENT_ALLOWED_TABS = [
  ASSESSMENT_TAB_BOARD,
  ASSESSMENT_TAB_LIVE,
  ASSESSMENT_TAB_FINAL,
  ASSESSMENT_TAB_STATIONS,
  ASSESSMENT_TAB_MINE,
  ASSESSMENT_TAB_MANAGE,
];
const ASSESSMENT_PDF_OPTIONS = [
  {
    value: 'assessment-report-all',
    label: 'Gesamtbericht Eignungsverfahren',
    requiresParticipantSelection: false,
    singlePdfPerParticipant: false,
  },
  {
    value: 'assessment-report-selection',
    label: 'Sammelbericht Teilnehmer',
    requiresParticipantSelection: true,
    singlePdfPerParticipant: false,
  },
  {
    value: 'assessment-report-single',
    label: 'Einzelbericht Teilnehmer',
    requiresParticipantSelection: true,
    singlePdfPerParticipant: true,
  },
];

function formatDuration(timeMs) {
  if (!Number.isFinite(Number(timeMs)) || Number(timeMs) < 0) {
    return '00:00';
  }

  const totalSeconds = Math.round(Number(timeMs) / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function roundToSecondMs(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return Math.round(numeric / 1000) * 1000;
}

function parseMmSsToMs(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds) || seconds > 59) {
    return null;
  }

  return (minutes * 60 + seconds) * 1000;
}

function getQualifiedChipProps(qualified) {
  if (qualified === true) {
    return { label: 'Bestanden', color: 'success' };
  }
  if (qualified === false) {
    return { label: 'Nicht bestanden', color: 'error' };
  }
  return { label: 'Noch offen', color: 'warning' };
}

function getStationStatusChipProps(status) {
  if (status === 'final') return { label: 'Final', color: 'success' };
  if (status === 'saved') return null;
  if (status === 'in_progress') return { label: 'In Bearbeitung', color: 'warning' };
  return { label: 'Offen', color: 'default' };
}

function getProgressTone(openStationCount) {
  if (!openStationCount) return 'success';
  if (openStationCount <= 2) return 'warning';
  return 'default';
}

function getFailedStationsLabel(summary) {
  const failedStations = Array.isArray(summary?.stations)
    ? summary.stations.filter((station) => station?.passed === false)
    : [];

  if (!failedStations.length) {
    return '';
  }

  const failedTitles = failedStations
    .map((station) => station.shortTitle || station.title || station.code)
    .filter(Boolean);

  return failedTitles.length ? `Nicht bestanden: ${failedTitles.join(' / ')}` : '';
}

const ASSESSMENT_DRAFT_PREFIX = 'assessment-draft-v1';
const ASSESSMENT_EVENTS_CACHE_KEY = 'assessment-events-cache-v1';
const ASSESSMENT_OFFLINE_PACKAGE_PREFIX = 'assessment-offline-package-v1';
const ASSESSMENT_SYNC_QUEUE_KEY = 'assessment-sync-queue-v1';

function buildAssessmentDraftKey(eventId, candidateId, stationCode) {
  if (!eventId || !candidateId || !stationCode) {
    return '';
  }
  return `${ASSESSMENT_DRAFT_PREFIX}:${eventId}:${candidateId}:${stationCode}`;
}

function buildOfflinePackageKey(eventId) {
  if (!eventId) {
    return '';
  }
  return `${ASSESSMENT_OFFLINE_PACKAGE_PREFIX}:${eventId}`;
}

function stringifyDraftForm(formValue) {
  try {
    return JSON.stringify(formValue || {});
  } catch {
    return '';
  }
}

function readAssessmentDraft(draftKey) {
  if (!draftKey || typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(draftKey);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue || typeof parsedValue !== 'object' || typeof parsedValue.formData !== 'object') {
      window.localStorage.removeItem(draftKey);
      return null;
    }

    return parsedValue;
  } catch {
    try {
      window.localStorage.removeItem(draftKey);
    } catch {
      return null;
    }
    return null;
  }
}

function writeAssessmentDraft(draftKey, draftValue) {
  if (!draftKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(draftKey, JSON.stringify(draftValue));
  } catch {
    // Ignore browser storage failures; backend save remains the primary source of truth.
  }
}

function readStoredJson(storageKey) {
  if (!storageKey || typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    return null;
  }
}

function writeStoredJson(storageKey, value) {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore storage write failures.
  }
}

function readOfflinePackage(eventId) {
  return readStoredJson(buildOfflinePackageKey(eventId));
}

function writeOfflinePackage(eventId, value) {
  writeStoredJson(buildOfflinePackageKey(eventId), value);
}

function readAssessmentEventsCache() {
  const cachedValue = readStoredJson(ASSESSMENT_EVENTS_CACHE_KEY);
  return Array.isArray(cachedValue?.events) ? cachedValue.events : [];
}

function writeAssessmentEventsCache(events) {
  writeStoredJson(ASSESSMENT_EVENTS_CACHE_KEY, { events: Array.isArray(events) ? events : [] });
}

function readSyncQueue() {
  const cachedValue = readStoredJson(ASSESSMENT_SYNC_QUEUE_KEY);
  return Array.isArray(cachedValue) ? cachedValue : [];
}

function writeSyncQueue(queueItems) {
  writeStoredJson(ASSESSMENT_SYNC_QUEUE_KEY, Array.isArray(queueItems) ? queueItems : []);
}

function upsertSyncQueueItem(nextItem) {
  const queueItems = readSyncQueue();
  const filteredItems = queueItems.filter((item) => !(
    String(item.eventId) === String(nextItem.eventId)
    && String(item.candidateId) === String(nextItem.candidateId)
    && String(item.stationCode) === String(nextItem.stationCode)
  ));
  filteredItems.push(nextItem);
  writeSyncQueue(filteredItems);
  return filteredItems;
}

function listPreparedOfflineEvents() {
  if (typeof window === 'undefined') {
    return [];
  }

  return Object.keys(window.localStorage)
    .filter((storageKey) => storageKey.startsWith(`${ASSESSMENT_OFFLINE_PACKAGE_PREFIX}:`))
    .map((storageKey) => readStoredJson(storageKey))
    .filter((item) => item?.event?.id)
    .map((item) => ({
      eventId: String(item.event.id),
      label: item.event.label || 'Eignungsverfahren',
      cachedAt: item.cachedAt || null,
      candidateCount: Array.isArray(item.candidates) ? item.candidates.length : 0,
    }))
    .sort((left, right) => String(left.label || '').localeCompare(String(right.label || ''), 'de'));
}

function formatSyncQueueItemLabel(item) {
  if (!item) {
    return '';
  }

  const stationTitle = item.stationTitle || item.stationCode || 'Stationsbogen';
  const candidateName = item.candidateName || 'Unbekannter Bewerber';
  return `${candidateName} · ${stationTitle}`;
}

function formatSyncIssueMessage(item) {
  if (!item?.lastError) {
    return 'Lokale Änderung konnte noch nicht synchronisiert werden.';
  }

  return item.lastError;
}

function removeSyncQueueItem(eventId, candidateId, stationCode) {
  const queueItems = readSyncQueue().filter((item) => !(
    String(item.eventId) === String(eventId)
    && String(item.candidateId) === String(candidateId)
    && String(item.stationCode) === String(stationCode)
  ));
  writeSyncQueue(queueItems);
  return queueItems;
}

function removeAssessmentDraft(draftKey) {
  if (!draftKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(draftKey);
  } catch {
    // Ignore storage cleanup failures.
  }
}

function formatDraftTimestamp(value) {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toLocaleString('de-DE');
}

function isNetworkLikeError(error) {
  return !error?.response;
}

function formatFailureReason(code) {
  const labels = {
    time_limit_exceeded: 'Zeitlimit überschritten',
    knots_not_passed: 'Knoten nicht bestanden',
    deductions_exceeded: 'Zu viele Fehlerabzüge',
    wrongBinding: 'Falsche Einbindung',
    wrongAnchorPoint: 'Falscher Festpunkt',
    fatalMistake: 'Schwerer Fehler',
    ladder_run_failed: 'Ein Durchgang nicht bestanden',
    pause_limit_exceeded: 'Pausenlimit überschritten',
    ladder_two_falls: 'Zwei mal gestürzt',
    minimum_points_not_reached: 'Mindestpunktzahl nicht erreicht',
    not_suitable_for_exposed_work: 'Nicht geeignet für Arbeiten in exponierten Lange',
    question_1_failed: 'Frage 1 nicht bestanden',
    question_2_failed: 'Frage 2 nicht bestanden',
    question_3_failed: 'Frage 3 nicht bestanden',
    question_4_failed: 'Frage 4 nicht bestanden',
  };

  return labels[code] || code || '';
}

function formatMyStationFeedback(stationResult) {
  const score = stationResult?.scorePayload || {};
  const entries = [];
  const addFeedback = (category, text) => {
    entries.push({ category, text });
  };

  if (!score.scoringMode) {
    return entries;
  }

  if (score.scoringMode === 'knots') {
    (score.methods || []).forEach((method) => {
      if (method.timeExceeded) {
        addFeedback('Zeit', `${method.label}: Zeitlimit überschritten.`);
      }
      if (Number(method.additionalMinorErrors || 0) > 0) {
        addFeedback('Technik', `${method.label}: Falsche Reihenfolge (${method.additionalMinorErrors}x, -${method.additionalMinorErrors}).`);
      }
      (method.knots || []).forEach((knot) => {
        (knot.items || []).forEach((item) => {
          if (Number(item.count || 0) > 0) {
            const deduction = Number(item.deduction || 0);
            addFeedback('Technik', `${method.label} / ${knot.label}: ${item.label} (${item.count}x, -${deduction}).`);
          }
        });
      });
    });
    return entries;
  }

  if (score.scoringMode === 'time_deductions') {
    if (score.timeExceeded) {
      addFeedback('Zeit', 'Zeitlimit überschritten.');
    }
    const fatalFlags = score.fatalFlags || {};
    Object.entries(fatalFlags).forEach(([key, value]) => {
      if (value) {
        addFeedback('Ausschluss', `Ausschlusskriterium aktiv: ${formatFailureReason(key) || key}.`);
      }
    });
    (score.deductions || []).forEach((group) => {
      (group.items || []).forEach((item) => {
        if (Number(item.count || 0) > 0) {
          addFeedback('Technik', `${group.label}: ${item.label} (${item.count}x, -${item.deduction}).`);
        }
      });
    });
    return entries;
  }

  if (score.scoringMode === 'ladder') {
    if (score.twoFalls) {
      addFeedback('Ausschluss', 'Zwei mal gestürzt (Ausschlusskriterium).');
    }
    ['run1', 'run2'].forEach((runKey, index) => {
      const run = score[runKey];
      if (!run) return;
      const runLabel = `Durchgang ${index + 1}`;
      if (!run.passed) {
        addFeedback('Zeit', `${runLabel}: Zeitlimit überschritten.`);
      }
      if (run.majorInsecurity) addFeedback('Sicherheit', `${runLabel}: Deutliche Unsicherheit (-5).`);
      if (run.minorInsecurity) addFeedback('Sicherheit', `${runLabel}: Geringe Unsicherheit (-3).`);
      if (Number(run.pauseCount || 0) > 0) addFeedback('Sicherheit', `${runLabel}: Pause (${run.pauseCount}x, -${run.pauseCount}).`);
      if (run.pullUp === 'incomplete') addFeedback('Technik', `${runLabel}: Klimmzug unvollständig (-1).`);
      if (run.pullUp === 'hinted') addFeedback('Technik', `${runLabel}: Klimmzug nur angedeutet (-3).`);
      if (run.pullUp === 'notDone') addFeedback('Technik', `${runLabel}: Klimmzug nicht gemacht (-5).`);
    });
    return entries;
  }

  if (score.scoringMode === 'matrix') {
    if (score.timeLimitExceeded) {
      addFeedback('Zeit', 'Zeitlimit überschritten.');
    }
    if (score.suitableForExposedWork === false) {
      addFeedback('Pflichtkriterium', 'Nicht geeignet für Arbeiten in exponierter Lage bewertet.');
    }
    (score.criteria || []).forEach((criterion) => {
      const missed = 8 - Number(criterion.points || 0);
      if (missed > 0) {
        addFeedback('Technik', `${criterion.label}: ${criterion.points} statt 8 Punkte (-${missed}).`);
      }
    });
    return entries;
  }

  if (score.scoringMode === 'station6') {
    const q4Only = score.station6QuestionSet === 'question4_only';
    if (!q4Only) {
      if (score.question1?.passed === false) addFeedback('Pflichtkriterium', 'Frage 1 nicht bestanden.');
      if (score.question2?.passed === false) addFeedback('Pflichtkriterium', 'Frage 2 nicht bestanden.');
      if (score.question3Preparation?.passed === false) addFeedback('Pflichtkriterium', 'Frage 3 nicht bestanden.');
    }
    if (score.question4?.passed === false) addFeedback('Pflichtkriterium', 'Frage 4 nicht bestanden.');
    return entries;
  }

  return entries;
}

function formatDisplayDate(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[3]}.${match[2]}.${match[1]}`;
  }
  return raw || '--.--.----';
}

function getAssessmentEventStatusLabel(status) {
  if (status === 'active') {
    return 'Laufendes Verfahren';
  }
  if (status === 'draft') {
    return 'Entwurf';
  }
  if (status === 'closed') {
    return 'Abgeschlossen';
  }
  return status || 'Unbekannt';
}

function getAssessmentEventStatusChipColor(status) {
  if (status === 'active') {
    return 'success';
  }
  if (status === 'draft') {
    return 'warning';
  }
  return 'default';
}

function getAssigneeRoleLabel(value) {
  const labels = {
    candidate_internal: 'Bewerber intern',
    candidate_external: 'Bewerber extern',
    examiner_pool: 'Pruefer',
    observer: 'Beobachter',
  };

  return labels[value] || value || 'Unbekannt';
}

function getStationFiveCriterionOptions(criterionId) {
  const optionsByCriterion = {
    anchoring: [
      { points: 8, label: 'fluessig/sicher/1. Versuch' },
      { points: 6, label: 'leichte Schwierigk./2. Versuch' },
      { points: 4, label: '3. Versuch' },
      { points: 2, label: 'Hilfestellung' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    positioning: [
      { points: 8, label: 'fluessig/sicher' },
      { points: 6, label: 'hektisch/unsicher' },
      { points: 4, label: 'deutlich unsicher' },
      { points: 2, label: 'Hilfestellung' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    stanceSafety: [
      { points: 8, label: 'fluessig/sicher/1. Versuch' },
      { points: 6, label: 'leichte Schwierigk./2. Versuch' },
      { points: 4, label: '3. Versuch/unsicher' },
      { points: 2, label: 'Hilfestellung' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    boardSafety: [
      { points: 8, label: 'Kein Absturz moeglich durch Karabiner o. ae.' },
      { points: 4, label: 'haendisch' },
      { points: 2, label: 'Zaehne, Arm etc.' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    unscrewing: [
      { points: 8, label: 'geplant sicher' },
      { points: 6, label: 'ungeplant' },
      { points: 2, label: 'Brett rutscht, faellt aber nicht' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    screwing: [
      { points: 8, label: 'geplant sicher' },
      { points: 6, label: 'mehrere Ansaetze' },
      { points: 2, label: 'Brett rutscht, faellt aber nicht' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    passiveDescent: [
      { points: 8, label: 'fluessig/sicher' },
      { points: 4, label: 'muss sich ueberwinden' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    insertHms: [
      { points: 8, label: 'mit erstem Versuch eingelegt' },
      { points: 6, label: 'zwei Versuche' },
      { points: 4, label: 'mehrere Versuche' },
      { points: 2, label: 'Hilfestellung' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    descent: [
      { points: 8, label: 'sicher/paralleler Seilverlauf' },
      { points: 6, label: 'leichte Schwierigk./2. Versuch' },
      { points: 4, label: 'mehrere Versuche' },
      { points: 2, label: 'Hilfestellung' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
    groundContact: [
      { points: 8, label: 'sicher' },
      { points: 6, label: 'unsicher' },
      { points: 4, label: 'stolpern' },
      { points: 2, label: 'im Sitzen' },
      { points: 0, label: 'Nur mit Hilfe' },
    ],
  };

  return optionsByCriterion[criterionId] || [];
}

function getPathValue(source, path) {
  return String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((current, segment) => (current == null ? undefined : current[segment]), source);
}

function setPathValue(source, path, value) {
  const segments = String(path || '').split('.').filter(Boolean);
  if (!segments.length) {
    return source;
  }

  const clone = Array.isArray(source) ? [...source] : { ...(source || {}) };
  let cursor = clone;

  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    const nextValue = cursor[segment];
    cursor[segment] = Array.isArray(nextValue) ? [...nextValue] : { ...(nextValue || {}) };
    cursor = cursor[segment];
  }

  cursor[segments[segments.length - 1]] = value;
  return clone;
}

function createKnotsForm(definition, rawPayload = {}, station) {
  const methods = Object.fromEntries((definition.methods || []).map((method) => [
    method.id,
    {
      timeMs: rawPayload.methods?.[method.id]?.timeMs == null ? '' : String(rawPayload.methods[method.id].timeMs),
      additionalMinorErrors: rawPayload.methods?.[method.id]?.additionalMinorErrors == null ? '0' : String(rawPayload.methods[method.id].additionalMinorErrors),
      knots: Object.fromEntries((definition.knots || []).map((knot) => [
        knot.id,
        Object.fromEntries((definition.deductionItems || []).map((item) => {
          const rawValue = rawPayload.methods?.[method.id]?.knots?.[knot.id]?.[item.id];
          return [item.id, item.type === 'boolean' ? Boolean(rawValue) : String(rawValue == null ? '0' : rawValue)];
        }))
      ])),
    },
  ]));

  return {
    methods,
    remarks: rawPayload.remarks || station?.remarks || '',
  };
}

function createTimeDeductionForm(definition, rawPayload = {}, station) {
  return {
    timeMs: rawPayload.timeMs == null ? '' : String(rawPayload.timeMs),
    groups: Object.fromEntries((definition.groups || []).map((group) => [
      group.id,
      Object.fromEntries(group.items.map((item) => [item.id, Boolean(rawPayload.groups?.[group.id]?.[item.id])]))
    ])),
    ...Object.fromEntries((definition.fatalRules || []).map((rule) => [rule.id, Boolean(rawPayload[rule.id])])),
    remarks: rawPayload.remarks || station?.remarks || '',
  };
}

function createLadderForm(rawPayload = {}, station) {
  const buildRun = (runPayload = {}) => ({
    timeMs: runPayload.timeMs == null ? '' : String(runPayload.timeMs),
    majorInsecurity: Boolean(runPayload.majorInsecurity),
    minorInsecurity: Boolean(runPayload.minorInsecurity),
    pauseCount: runPayload.pauseCount == null ? '0' : String(runPayload.pauseCount),
    pullUp: runPayload.pullUp || 'none',
  });

  return {
    run1: buildRun(rawPayload.run1),
    pauseBetweenRunsMs: rawPayload.pauseBetweenRunsMs == null ? '' : String(rawPayload.pauseBetweenRunsMs),
    run2: buildRun(rawPayload.run2),
    twoFalls: Boolean(rawPayload.twoFalls),
    remarks: rawPayload.remarks || station?.remarks || '',
  };
}

function createMatrixForm(definition, rawPayload = {}, station) {
  return {
    timeMs: rawPayload.timeMs == null ? '' : String(rawPayload.timeMs),
    criteria: Object.fromEntries((definition.criteria || []).map((criterion) => [
      criterion.id,
      rawPayload.criteria?.[criterion.id] == null ? '' : String(rawPayload.criteria[criterion.id]),
    ])),
    suitableForExposedWork:
      typeof rawPayload.suitableForExposedWork === 'boolean'
        ? rawPayload.suitableForExposedWork
        : null,
    remarks: rawPayload.remarks || station?.remarks || '',
  };
}

function createStationSixForm(rawPayload = {}, station) {
  return {
    question1Keywords: Array.isArray(rawPayload.question1Keywords) ? rawPayload.question1Keywords : [],
    question2CorePointMentioned: Boolean(rawPayload.question2CorePointMentioned),
    question2AdditionalNotes: rawPayload.question2AdditionalNotes || '',
    question3PreparationKeywords: Array.isArray(rawPayload.question3PreparationKeywords) ? rawPayload.question3PreparationKeywords : [],
    question3PreparationAdditionalNotes: rawPayload.question3PreparationAdditionalNotes || '',
    question4ScenarioId: rawPayload.question4ScenarioId || rawPayload.question3ScenarioId || 'A',
    question4Keywords: Array.isArray(rawPayload.question4Keywords)
      ? rawPayload.question4Keywords
      : (Array.isArray(rawPayload.question3Keywords) ? rawPayload.question3Keywords : []),
    remarks: rawPayload.remarks || station?.remarks || '',
  };
}

function buildInitialForm(definition, station) {
  const rawPayload = station?.rawPayload || {};
  if (!definition) {
    return {};
  }

  if (definition.scoringMode === 'station6') {
    return createStationSixForm(rawPayload, station);
  }

  if (definition.scoringMode === 'knots') {
    return createKnotsForm(definition, rawPayload, station);
  }

  if (definition.scoringMode === 'time_deductions') {
    return createTimeDeductionForm(definition, rawPayload, station);
  }

  if (definition.scoringMode === 'ladder') {
    return createLadderForm(rawPayload, station);
  }

  if (definition.scoringMode === 'matrix') {
    return createMatrixForm(definition, rawPayload, station);
  }

  return { remarks: rawPayload.remarks || station?.remarks || '' };
}

function EignungsverfahrenPage() {
  const { user } = useAuth();
  const isEditor = canEditAssessments(user);
  const isCandidate = hasAssessmentCandidateRole(user);
  const isObserver = isAssessmentObserver(user);
  const canManage = canManageAssessmentEvents(user);
  const canSeeLiveResults = canViewAssessmentLiveResults(user);
  const showBoardTab = isEditor;
  const showLiveTab = canSeeLiveResults;
  const showFinalTab = canSeeLiveResults;
  const showStationsTab = isCandidate || isObserver || isEditor;
  const showMineTab = isCandidate && !isEditor;
  const showManageTab = canManage;
  const defaultTab = showBoardTab
    ? ASSESSMENT_TAB_BOARD
    : showLiveTab
      ? ASSESSMENT_TAB_LIVE
      : showMineTab
        ? ASSESSMENT_TAB_MINE
        : ASSESSMENT_TAB_STATIONS;
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventDetail, setEventDetail] = useState(null);
  const [stations, setStations] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [selectedStationCode, setSelectedStationCode] = useState('station_1_knots');
  const [stationDetail, setStationDetail] = useState(null);
  const [stationFormDefinition, setStationFormDefinition] = useState(null);
  const [liveResults, setLiveResults] = useState([]);
  const [finalResults, setFinalResults] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [assignmentOptions, setAssignmentOptions] = useState([]);
  const [groupedAssignees, setGroupedAssignees] = useState({ candidates: [], examiners: [], observers: [] });
  const [selectedAssigneeUserIds, setSelectedAssigneeUserIds] = useState([]);
  const [eventForm, setEventForm] = useState({
    label: `Eignungsverfahren ${new Date().getFullYear()}`,
    year: String(new Date().getFullYear()),
    assessmentDate: '',
    notes: '',
  });
  const [unlockReason, setUnlockReason] = useState('');
  const [stationForm, setStationForm] = useState({});
  const [activeTab, setActiveTab] = useState(() => readPersistedTab(
    ASSESSMENT_ACTIVE_TAB_STORAGE_KEY,
    ASSESSMENT_ALLOWED_TABS,
    defaultTab,
  ));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exportingEventPdf, setExportingEventPdf] = useState(false);
  const [selectedAssessmentPdfVariant, setSelectedAssessmentPdfVariant] = useState('assessment-report-all');
  const [selectedAssessmentPdfCandidateIds, setSelectedAssessmentPdfCandidateIds] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerStartReference, setTimerStartReference] = useState(null);
  const [timerTargetPath, setTimerTargetPath] = useState('');
  const [timerTargetLabel, setTimerTargetLabel] = useState('');
  const [liveElapsedMs, setLiveElapsedMs] = useState(0);
  const [timerResetDialog, setTimerResetDialog] = useState({ open: false, path: '', label: '' });
  const [manualTimeDialog, setManualTimeDialog] = useState({
    open: false,
    path: '',
    label: '',
    value: '00:00',
    error: '',
  });
  const [myStationDialog, setMyStationDialog] = useState({
    open: false,
    eventLabel: '',
    stationTitle: '',
    stationResult: null,
  });
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [draftNotice, setDraftNotice] = useState(null);
  const [pendingDraftRecovery, setPendingDraftRecovery] = useState(null);
  const [draftSyncEnabled, setDraftSyncEnabled] = useState(false);
  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [syncQueueItems, setSyncQueueItems] = useState(() => readSyncQueue());
  const [offlinePreparedEvents, setOfflinePreparedEvents] = useState(() => listPreparedOfflineEvents());
  const serverFormSnapshotRef = useRef('');

  const activeStationDefinition = useMemo(
    () => stations.find((station) => station.code === selectedStationCode) || null,
    [stations, selectedStationCode]
  );
  const isFinalizedStation = stationDetail?.status === 'final';
  const isStationReadOnly = isFinalizedStation;

  const activeCandidate = useMemo(
    () => candidates.find((candidate) => String(candidate.eventCandidateId) === String(selectedCandidateId)) || null,
    [candidates, selectedCandidateId]
  );
  const currentDraftKey = useMemo(
    () => buildAssessmentDraftKey(selectedEventId, selectedCandidateId, selectedStationCode),
    [selectedEventId, selectedCandidateId, selectedStationCode]
  );
  const syncQueueCount = syncQueueItems.length;
  const isCurrentEventPreparedOffline = useMemo(
    () => offlinePreparedEvents.some((item) => item.eventId === String(selectedEventId || '')),
    [offlinePreparedEvents, selectedEventId]
  );
  const currentPreparedOfflineEvent = useMemo(
    () => offlinePreparedEvents.find((item) => item.eventId === String(selectedEventId || '')) || null,
    [offlinePreparedEvents, selectedEventId]
  );

  const station6Score = stationDetail?.scorePayload || {};
  const stationSixDefinition = useMemo(() => {
    if (stationFormDefinition?.code === selectedStationCode) {
      return stationFormDefinition;
    }
    return activeStationDefinition;
  }, [stationFormDefinition, selectedStationCode, activeStationDefinition]);
  const isStation6Question4Only = stationSixDefinition?.station6QuestionSet === 'question4_only';
  const visibleTabs = useMemo(() => {
    const tabs = [];
    if (showBoardTab) tabs.push('board');
    if (showLiveTab) tabs.push('live');
    if (showFinalTab) tabs.push('final');
    if (showStationsTab) tabs.push('stations');
    if (showMineTab) tabs.push('mine');
    if (showManageTab) tabs.push('manage');
    return tabs;
  }, [showBoardTab, showLiveTab, showFinalTab, showStationsTab, showMineTab, showManageTab]);
  const selectedAssessmentPdfOption = useMemo(
    () => ASSESSMENT_PDF_OPTIONS.find((option) => option.value === selectedAssessmentPdfVariant) || ASSESSMENT_PDF_OPTIONS[0],
    [selectedAssessmentPdfVariant],
  );
  const assessmentPdfCandidateNameById = useMemo(
    () => new Map((candidates || []).map((candidate) => [String(candidate.eventCandidateId), candidate.fullName || '-'])),
    [candidates],
  );

  useEffect(() => {
    const validCandidateIds = new Set((candidates || []).map((candidate) => String(candidate.eventCandidateId)));
    setSelectedAssessmentPdfCandidateIds((currentValue) => currentValue.filter((candidateId) => validCandidateIds.has(String(candidateId))));
  }, [candidates]);

  const applyOfflinePackage = useCallback((offlinePackage) => {
    if (!offlinePackage) {
      return false;
    }

    setEventDetail(offlinePackage.event || null);
    setStations(offlinePackage.stations || []);
    setCandidates((offlinePackage.candidates || []).map((item) => item.candidate));
    setLiveResults([]);
    setFinalResults([]);
    setAssignmentOptions([]);
    setGroupedAssignees({ candidates: [], examiners: [], observers: [] });
    return true;
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const response = await client.get('/api/assessments/events');
      const nextEvents = response.data?.events || [];
      setEvents(nextEvents);
      writeAssessmentEventsCache(nextEvents);
      if (!selectedEventId && nextEvents.length > 0) {
        const activeEvent = nextEvents.find((event) => event.status === 'active') || nextEvents[0];
        setSelectedEventId(String(activeEvent.id));
      }
      return;
    } catch (error) {
      const cachedEvents = readAssessmentEventsCache();
      if (!cachedEvents.length) {
        throw error;
      }

      setEvents(cachedEvents);
      if (!selectedEventId && cachedEvents.length > 0) {
        const activeEvent = cachedEvents.find((event) => event.status === 'active') || cachedEvents[0];
        setSelectedEventId(String(activeEvent.id));
      }
      setDraftNotice({ severity: 'warning', message: 'Keine Verbindung zum Server. Vorbereitete Verfahren werden lokal geladen.' });
    }
  }, [selectedEventId]);

  const loadEventBundle = useCallback(async (eventId) => {
    if (!eventId) return;

    try {
      const [eventRes, candidatesRes] = await Promise.all([
        client.get(`/api/assessments/events/${eventId}`),
        client.get(`/api/assessments/events/${eventId}/candidates`),
      ]);

      setEventDetail(eventRes.data?.event || null);
      setStations(eventRes.data?.stations || []);
      const nextCandidates = candidatesRes.data?.candidates || [];
      setCandidates(nextCandidates);

      if (nextCandidates.length > 0) {
        setSelectedCandidateId((current) => {
          const existing = nextCandidates.some((candidate) => String(candidate.eventCandidateId) === String(current));
          return existing ? current : String(nextCandidates[0].eventCandidateId);
        });
      } else {
        setSelectedCandidateId('');
      }

      if (canSeeLiveResults) {
        const [liveRes, finalRes] = await Promise.all([
          client.get(`/api/assessments/events/${eventId}/live-results`),
          client.get(`/api/assessments/events/${eventId}/final-results`),
        ]);
        setLiveResults(liveRes.data?.ranking || []);
        setFinalResults(finalRes.data?.completedOnly || []);
      } else {
        setLiveResults([]);
        setFinalResults([]);
      }

      if (canManage) {
        const [optionsRes, assigneesRes] = await Promise.all([
          client.get(`/api/assessments/events/${eventId}/assignment-options`),
          client.get(`/api/assessments/events/${eventId}/assignees`),
        ]);
        setAssignmentOptions(optionsRes.data?.users || []);
        setGroupedAssignees(assigneesRes.data?.groups || { candidates: [], examiners: [], observers: [] });
      } else {
        setAssignmentOptions([]);
        setGroupedAssignees({ candidates: [], examiners: [], observers: [] });
      }
      return;
    } catch (error) {
      const offlinePackage = readOfflinePackage(eventId);
      if (!applyOfflinePackage(offlinePackage)) {
        throw error;
      }

      const offlineCandidates = (offlinePackage?.candidates || []).map((item) => item.candidate);
      if (offlineCandidates.length > 0) {
        setSelectedCandidateId((current) => {
          const existing = offlineCandidates.some((candidate) => String(candidate.eventCandidateId) === String(current));
          return existing ? current : String(offlineCandidates[0].eventCandidateId);
        });
      } else {
        setSelectedCandidateId('');
      }
      setDraftNotice({ severity: 'warning', message: 'Keine Verbindung zum Server. Vorbereitetes Verfahren wurde lokal geladen.' });
    }
  }, [applyOfflinePackage, canManage, canSeeLiveResults]);

  const loadMyResults = useCallback(async () => {
    const response = await client.get('/api/assessments/my-results');
    setMyResults(response.data?.results || []);
  }, []);

  const resetTimerState = useCallback(() => {
    setTimerRunning(false);
    setTimerStartReference(null);
    setTimerTargetPath('');
    setTimerTargetLabel('');
    setLiveElapsedMs(0);
  }, []);

  const restorePersistentTimer = useCallback((persistedTimer) => {
    const restoredTimer = readAssessmentPersistentTimer(persistedTimer);
    if (!restoredTimer) {
      resetTimerState();
      return;
    }

    setTimerTargetPath(restoredTimer.targetPath);
    setTimerTargetLabel(restoredTimer.targetLabel);
    setTimerStartReference(restoredTimer.startReference);
    setLiveElapsedMs(getElapsedMsFromStartReference(restoredTimer.startReference));
    setTimerRunning(true);
  }, [resetTimerState]);

  const getCurrentRunningTimerState = useCallback(() => (
    buildAssessmentPersistentTimer({
      running: timerRunning,
      startReference: timerStartReference,
      targetPath: timerTargetPath,
      targetLabel: timerTargetLabel,
    })
  ), [timerRunning, timerStartReference, timerTargetPath, timerTargetLabel]);

  const loadStation = useCallback(async (eventId, candidateId, stationCode) => {
    if (!eventId || !candidateId || !stationCode || !isEditor) {
      setStationDetail(null);
      setStationFormDefinition(null);
      setPendingDraftRecovery(null);
      setDraftSyncEnabled(false);
      resetTimerState();
      return;
    }

    setStationFormDefinition(null);
    setPendingDraftRecovery(null);
    setDraftSyncEnabled(false);
    const response = await client.get(`/api/assessments/events/${eventId}/candidates/${candidateId}/stations/${stationCode}`);
    const nextStation = response.data?.station || null;
    const nextDefinition = response.data?.definition || null;
    const nextForm = buildInitialForm(nextDefinition, nextStation);
    const draftKey = buildAssessmentDraftKey(eventId, candidateId, stationCode);
    const existingDraft = readAssessmentDraft(draftKey);
    const serverSnapshot = stringifyDraftForm(nextForm);

    setStationDetail(nextStation);
    setStationFormDefinition(nextDefinition);
    setStationForm(nextForm);
    setUnlockReason('');
    serverFormSnapshotRef.current = serverSnapshot;
    resetTimerState();

    if (nextStation?.timerSession) {
      restorePersistentTimer(nextStation.timerSession);
    }

    if (nextStation?.status === 'final') {
      removeAssessmentDraft(draftKey);
      return;
    }

    if (existingDraft?.formData && stringifyDraftForm(existingDraft.formData) !== serverSnapshot) {
      setPendingDraftRecovery({ key: draftKey, draft: existingDraft });
      setDraftNotice({ severity: 'info', message: 'Lokaler Entwurf gefunden.' });
      return;
    }

    if (existingDraft) {
      removeAssessmentDraft(draftKey);
    }

    setDraftSyncEnabled(true);
  }, [isEditor, resetTimerState, restorePersistentTimer]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    setOfflinePreparedEvents(listPreparedOfflineEvents());
    setSyncQueueItems(readSyncQueue());

    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] || defaultTab);
    }
  }, [activeTab, defaultTab, visibleTabs]);

  useEffect(() => {
    const nextTab = visibleTabs.includes(activeTab) ? activeTab : (visibleTabs[0] || defaultTab);
    writePersistedTab(ASSESSMENT_ACTIVE_TAB_STORAGE_KEY, nextTab, ASSESSMENT_ALLOWED_TABS);
  }, [activeTab, defaultTab, visibleTabs]);

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      try {
        setLoading(true);
        setError('');
        await loadEvents();
        if (showMineTab) {
          await loadMyResults();
        }
      } catch (loadError) {
        if (mounted) {
          setError(loadError.response?.data?.error || 'Eignungsverfahren konnten nicht geladen werden.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      mounted = false;
    };
  }, [showMineTab, loadEvents, loadMyResults]);

  useEffect(() => {
    if (!selectedEventId) {
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        setError('');
        await loadEventBundle(selectedEventId);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.response?.data?.error || 'Verfahrensdaten konnten nicht geladen werden.');
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, loadEventBundle]);

  useEffect(() => {
    if (!selectedEventId || !selectedCandidateId || !selectedStationCode || !isEditor) {
      return;
    }

    loadStation(selectedEventId, selectedCandidateId, selectedStationCode).catch((loadError) => {
      if (isNetworkLikeError(loadError) && !ASSESSMENT_OFFLINE_MODE_ENABLED) {
        setStationDetail(null);
        setStationFormDefinition(null);
        setPendingDraftRecovery(null);
        setDraftSyncEnabled(false);
        resetTimerState();
        setError('Offlinebetrieb für das Eignungsverfahren ist derzeit deaktiviert. Eine Serververbindung ist erforderlich.');
        return;
      }

      const localDraft = readAssessmentDraft(buildAssessmentDraftKey(selectedEventId, selectedCandidateId, selectedStationCode));
      if (isNetworkLikeError(loadError) && localDraft?.formData && activeStationDefinition) {
        setStationDetail((current) => current || {
          id: null,
          status: 'saved',
          points: 0,
          max_points: activeStationDefinition.maxPoints,
          passed: null,
          rawPayload: localDraft.formData,
          scorePayload: {},
        });
        setStationFormDefinition(activeStationDefinition);
        setStationForm(localDraft.formData);
        setPendingDraftRecovery(null);
        setDraftSyncEnabled(true);
        serverFormSnapshotRef.current = '';
        resetTimerState();
        setError('');
        setDraftNotice({ severity: 'warning', message: 'Keine Verbindung zum Server. Lokaler Entwurf wurde geladen.' });
        return;
      }

      const offlinePackage = readOfflinePackage(selectedEventId);
      const offlineCandidate = (offlinePackage?.candidates || []).find((item) => (
        String(item?.candidateMeta?.event_candidate_id) === String(selectedCandidateId)
        || String(item?.candidate?.eventCandidateId) === String(selectedCandidateId)
      ));
      const offlineStation = (offlineCandidate?.stations || []).find((item) => item.stationCode === selectedStationCode);
      if (isNetworkLikeError(loadError) && offlineStation) {
        const cachedForm = buildInitialForm(offlineStation.definition, offlineStation.station);
        setStationDetail(offlineStation.station);
        setStationFormDefinition(offlineStation.definition);
        setStationForm(cachedForm);
        setPendingDraftRecovery(null);
        setDraftSyncEnabled(true);
        serverFormSnapshotRef.current = stringifyDraftForm(cachedForm);
        resetTimerState();
        setError('');
        setDraftNotice({ severity: 'warning', message: 'Keine Verbindung zum Server. Vorbereiteter Stationsbogen wurde lokal geladen.' });
        return;
      }

      setError(loadError.response?.data?.error || 'Stationsbogen konnte nicht geladen werden.');
    });
  }, [selectedEventId, selectedCandidateId, selectedStationCode, isEditor, loadStation, activeStationDefinition, resetTimerState, restorePersistentTimer]);

  useEffect(() => {
    if (!timerRunning || !timerStartReference) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setLiveElapsedMs(Date.now() - timerStartReference);
    }, 250);

    return () => clearInterval(intervalId);
  }, [timerRunning, timerStartReference]);

  useEffect(() => {
    if (!canSeeLiveResults || !selectedEventId) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      client.get(`/api/assessments/events/${selectedEventId}/live-results`)
        .then((response) => setLiveResults(response.data?.ranking || []))
        .catch(() => undefined);
    }, 15000);

    return () => clearInterval(intervalId);
  }, [canSeeLiveResults, selectedEventId]);

  useEffect(() => {
    if (!draftSyncEnabled || !currentDraftKey || !isEditor || isStationReadOnly || pendingDraftRecovery) {
      return undefined;
    }

    const currentSnapshot = stringifyDraftForm(stationForm);
    if (!currentSnapshot) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (currentSnapshot === serverFormSnapshotRef.current) {
        removeAssessmentDraft(currentDraftKey);
        return;
      }

      writeAssessmentDraft(currentDraftKey, {
        formData: stationForm,
        updatedAt: new Date().toISOString(),
        eventId: selectedEventId,
        candidateId: selectedCandidateId,
        stationCode: selectedStationCode,
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    currentDraftKey,
    draftSyncEnabled,
    isEditor,
    isStationReadOnly,
    pendingDraftRecovery,
    selectedCandidateId,
    selectedEventId,
    selectedStationCode,
    stationForm,
  ]);

  const handleCreateEvent = async () => {
    try {
      setBusy(true);
      setError('');
      setSuccess('');
      const response = await client.post('/api/assessments/events', {
        label: eventForm.label,
        year: Number(eventForm.year),
        assessmentDate: eventForm.assessmentDate || null,
        notes: eventForm.notes,
      });
      await loadEvents();
      if (response.data?.event?.id) {
        setSelectedEventId(String(response.data.event.id));
      }
      setSuccess('Das Verfahren wurde angelegt.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Verfahren konnte nicht angelegt werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleActivateEvent = async () => {
    try {
      setBusy(true);
      setError('');
      setSuccess('');
      await client.post(`/api/assessments/events/${selectedEventId}/activate`);
      await loadEvents();
      await loadEventBundle(selectedEventId);
      setSuccess('Das Verfahren ist jetzt aktiv.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Verfahren konnte nicht aktiviert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEventId) {
      return;
    }

    try {
      setBusy(true);
      setError('');
      setSuccess('');
      await client.put(`/api/assessments/events/${selectedEventId}`, {
        label: eventForm.label,
        year: Number(eventForm.year),
        assessmentDate: eventForm.assessmentDate || null,
        notes: eventForm.notes,
      });
      await loadEvents();
      await loadEventBundle(selectedEventId);
      setSuccess('Das Verfahren wurde aktualisiert.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Verfahren konnte nicht aktualisiert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleCloseEvent = async () => {
    if (!selectedEventId) {
      return;
    }

    try {
      setBusy(true);
      setError('');
      setSuccess('');

      const response = await client.post(`/api/assessments/events/${selectedEventId}/close`);
      const exportId = Number(response.data?.pdfExportId || 0);

      if (Number.isFinite(exportId) && exportId > 0) {
        await redownloadDienstzeitProtokollPdf(exportId);
      }

      await loadEvents();
      await loadEventBundle(selectedEventId);
      setCloseDialogOpen(false);
      setSuccess('Das Verfahren wurde abgeschlossen und das Ergebnis-PDF erstellt.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Verfahren konnte nicht abgeschlossen werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleExportEventPdf = async () => {
    if (!selectedEventId) {
      return;
    }

    const selectedCandidateIds = selectedAssessmentPdfCandidateIds
      .map((candidateId) => Number(candidateId))
      .filter((candidateId) => Number.isInteger(candidateId) && candidateId > 0);

    if (selectedAssessmentPdfOption.requiresParticipantSelection && !selectedCandidateIds.length) {
      setError('Bitte zuerst mindestens einen Teilnehmer für den PDF-Export auswählen.');
      return;
    }

    try {
      setExportingEventPdf(true);
      setError('');
      setSuccess('');

      const exportCandidateGroups = selectedAssessmentPdfOption.singlePdfPerParticipant
        ? selectedCandidateIds.map((candidateId) => [candidateId])
        : [selectedAssessmentPdfOption.requiresParticipantSelection ? selectedCandidateIds : []];

      for (const eventCandidateIds of exportCandidateGroups) {
        const response = await exportAssessmentPdf(
          selectedEventId,
          eventCandidateIds.length ? { eventCandidateIds } : {},
        );
        downloadPdfResponse(response, 'Eignungsverfahren.pdf');
      }

      setSuccess(
        selectedAssessmentPdfOption.singlePdfPerParticipant
          ? `${exportCandidateGroups.length} Teilnehmer-PDFs wurden erstellt.`
          : 'Das Ergebnis-PDF wurde erstellt.',
      );
    } catch (requestError) {
      setError(await extractPdfErrorMessage(requestError, 'Ergebnis-PDF konnte nicht erstellt werden.'));
    } finally {
      setExportingEventPdf(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEventId) {
      return;
    }

    try {
      setBusy(true);
      setError('');
      setSuccess('');

      await client.delete(`/api/assessments/events/${selectedEventId}`);

      const nextEventId = events.find((item) => String(item.id) !== String(selectedEventId))?.id;
      await loadEvents();
      setSelectedEventId(nextEventId ? String(nextEventId) : '');
      setDeleteDialogOpen(false);
      setDeleteConfirmationInput('');
      setSuccess('Das Verfahren wurde geloescht.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Verfahren konnte nicht geloescht werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleAddAssignees = async () => {
    try {
      setBusy(true);
      setError('');
      setSuccess('');
      await client.post(`/api/assessments/events/${selectedEventId}/assignees`, {
        userIds: selectedAssigneeUserIds,
      });
      setSelectedAssigneeUserIds([]);
      await loadEventBundle(selectedEventId);
      setSuccess('Die ausgewählten Nutzer wurden dem Verfahren zugeordnet.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Nutzer konnten nicht zugeordnet werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveCandidate = async (eventCandidateId, fullName) => {
    if (!selectedEventId) {
      return;
    }

    const confirmed = window.confirm(`Soll ${fullName || 'dieser Bewerber'} wirklich aus dem Verfahren entfernt werden?`);
    if (!confirmed) {
      return;
    }

    try {
      setBusy(true);
      setError('');
      setSuccess('');
      await client.delete(`/api/assessments/events/${selectedEventId}/candidates/${eventCandidateId}`);
      await loadEventBundle(selectedEventId);
      setSuccess('Der Bewerber wurde aus dem Verfahren entfernt.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Bewerber konnte nicht entfernt werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAssignee = async (assigneeId, fullName, roleLabel) => {
    if (!selectedEventId) {
      return;
    }

    const confirmed = window.confirm(`Soll ${fullName || 'diese Zuordnung'} als ${roleLabel || 'Rolle'} wirklich entfernt werden?`);
    if (!confirmed) {
      return;
    }

    try {
      setBusy(true);
      setError('');
      setSuccess('');
      await client.delete(`/api/assessments/events/${selectedEventId}/assignees/${assigneeId}`);
      await loadEventBundle(selectedEventId);
      setSuccess('Die Zuordnung wurde entfernt.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Zuordnung konnte nicht entfernt werden.');
    } finally {
      setBusy(false);
    }
  };

  const refreshAfterStationChange = async () => {
    await loadEventBundle(selectedEventId);
    await loadStation(selectedEventId, selectedCandidateId, selectedStationCode);
    if (!isEditor) {
      await loadMyResults();
    }
  };

  useEffect(() => {
    if (!showManageTab || !eventDetail) {
      return;
    }

    setEventForm((current) => ({
      ...current,
      label: eventDetail.label || current.label,
      year: Number(eventDetail.year || current.year),
      assessmentDate: eventDetail.assessment_date || '',
      notes: eventDetail.notes || '',
    }));
  }, [eventDetail, showManageTab]);

  const updateFormPath = useCallback((path, value) => {
    if (isStationReadOnly) {
      return;
    }
    setStationForm((current) => setPathValue(current, path, value));
  }, [isStationReadOnly]);

  const toggleQuestionKeyword = useCallback((field, keywordId) => {
    if (isStationReadOnly) {
      return;
    }
    setStationForm((current) => {
      const currentValues = Array.isArray(current[field]) ? current[field] : [];
      const exists = currentValues.includes(keywordId);
      return {
        ...current,
        [field]: exists ? currentValues.filter((value) => value !== keywordId) : [...currentValues, keywordId],
      };
    });
  }, [isStationReadOnly]);

  const buildPayloadForSave = useCallback((formState = stationForm) => {
    if (!activeStationDefinition) {
      return {};
    }

    if (activeStationDefinition.scoringMode === 'station6') {
      return {
        question1Keywords: formState.question1Keywords || [],
        question2CorePointMentioned: Boolean(formState.question2CorePointMentioned),
        question2AdditionalNotes: formState.question2AdditionalNotes || '',
        question3PreparationKeywords: formState.question3PreparationKeywords || [],
        question3PreparationAdditionalNotes: formState.question3PreparationAdditionalNotes || '',
        question4ScenarioId: formState.question4ScenarioId || 'A',
        question4Keywords: formState.question4Keywords || [],
        remarks: formState.remarks || '',
      };
    }

    if (activeStationDefinition.scoringMode === 'knots') {
      return {
        methods: Object.fromEntries((activeStationDefinition.methods || []).map((method) => [
          method.id,
          {
            timeMs: formState.methods?.[method.id]?.timeMs === '' ? null : Number(formState.methods?.[method.id]?.timeMs),
            additionalMinorErrors: Number(formState.methods?.[method.id]?.additionalMinorErrors || 0),
            knots: Object.fromEntries((activeStationDefinition.knots || []).map((knot) => [
              knot.id,
              Object.fromEntries((activeStationDefinition.deductionItems || []).map((item) => [
                item.id,
                item.type === 'boolean'
                  ? Boolean(formState.methods?.[method.id]?.knots?.[knot.id]?.[item.id])
                  : Number(formState.methods?.[method.id]?.knots?.[knot.id]?.[item.id] || 0),
              ])),
            ])),
          },
        ])),
        remarks: formState.remarks || '',
      };
    }

    if (activeStationDefinition.scoringMode === 'time_deductions') {
      return {
        timeMs: formState.timeMs === '' ? null : Number(formState.timeMs),
        groups: Object.fromEntries((activeStationDefinition.groups || []).map((group) => [
          group.id,
          Object.fromEntries(group.items.map((item) => [item.id, Boolean(formState.groups?.[group.id]?.[item.id])]))
        ])),
        ...Object.fromEntries((activeStationDefinition.fatalRules || []).map((rule) => [rule.id, Boolean(formState[rule.id])])),
        remarks: formState.remarks || '',
      };
    }

    if (activeStationDefinition.scoringMode === 'ladder') {
      return {
        run1: {
          timeMs: formState.run1?.timeMs === '' ? null : Number(formState.run1?.timeMs),
          majorInsecurity: Boolean(formState.run1?.majorInsecurity),
          minorInsecurity: Boolean(formState.run1?.minorInsecurity),
          pauseCount: Number(formState.run1?.pauseCount || 0),
          pullUp: formState.run1?.pullUp || 'none',
        },
        pauseBetweenRunsMs: formState.pauseBetweenRunsMs === '' ? 0 : Number(formState.pauseBetweenRunsMs),
        run2: {
          timeMs: formState.run2?.timeMs === '' ? null : Number(formState.run2?.timeMs),
          majorInsecurity: Boolean(formState.run2?.majorInsecurity),
          minorInsecurity: Boolean(formState.run2?.minorInsecurity),
          pauseCount: Number(formState.run2?.pauseCount || 0),
          pullUp: formState.run2?.pullUp || 'none',
        },
        twoFalls: Boolean(formState.twoFalls),
        remarks: formState.remarks || '',
      };
    }

    if (activeStationDefinition.scoringMode === 'matrix') {
      return {
        timeMs: formState.timeMs === '' ? null : Number(formState.timeMs),
        criteria: Object.fromEntries((activeStationDefinition.criteria || []).map((criterion) => [
          criterion.id,
          formState.criteria?.[criterion.id] === '' ? null : Number(formState.criteria?.[criterion.id]),
        ])),
        suitableForExposedWork: activeStationDefinition.requiresSuitabilityDecision
          ? formState.suitableForExposedWork
          : undefined,
        remarks: formState.remarks || '',
      };
    }

    return { remarks: formState.remarks || '' };
  }, [activeStationDefinition, stationForm]);

  const handleSaveStation = async () => {
    if (isStationReadOnly) {
      setError('Dieser Stationsbogen ist finalisiert und muss erst über "Bogen entsperren" freigegeben werden.');
      return;
    }

    if (isOffline && !ASSESSMENT_OFFLINE_MODE_ENABLED) {
      setError('Offlinebetrieb für das Eignungsverfahren ist derzeit deaktiviert. Eine Serververbindung ist erforderlich.');
      return;
    }

    const hadLocalDraft = Boolean(currentDraftKey && readAssessmentDraft(currentDraftKey));

    try {
      setBusy(true);
      setError('');
      setSuccess('');
      setDraftNotice(null);
      await client.put(
        `/api/assessments/events/${selectedEventId}/candidates/${selectedCandidateId}/stations/${selectedStationCode}`,
        {
          rawPayload: buildPayloadForSave(),
          remarks: stationForm.remarks || '',
        }
      );
      serverFormSnapshotRef.current = stringifyDraftForm(stationForm);
      removeAssessmentDraft(currentDraftKey);
      const remainingQueueItems = removeSyncQueueItem(selectedEventId, selectedCandidateId, selectedStationCode);
      setSyncQueueItems(remainingQueueItems);
      setPendingDraftRecovery(null);
      setDraftSyncEnabled(true);
      await refreshAfterStationChange();
      setSuccess(hadLocalDraft ? 'Stationsbogen gespeichert. Lokaler Entwurf wurde entfernt.' : 'Stationsbogen gespeichert.');
    } catch (requestError) {
      if (isNetworkLikeError(requestError)) {
        if (!ASSESSMENT_OFFLINE_MODE_ENABLED) {
          setError('Offlinebetrieb für das Eignungsverfahren ist derzeit deaktiviert. Änderungen konnten nicht lokal zwischengespeichert werden.');
          throw requestError;
        }

        const queuedItems = upsertSyncQueueItem({
          eventId: selectedEventId,
          candidateId: selectedCandidateId,
          stationCode: selectedStationCode,
          eventLabel: eventDetail?.label || 'Eignungsverfahren',
          candidateName: activeCandidate?.fullName || 'Unbekannter Bewerber',
          stationTitle: activeStationDefinition?.shortTitle || activeStationDefinition?.title || selectedStationCode,
          rawPayload: buildPayloadForSave(),
          remarks: stationForm.remarks || '',
          updatedAt: new Date().toISOString(),
          syncStatus: 'pending',
          lastError: '',
        });
        setSyncQueueItems(queuedItems);
        setError('');
        setDraftNotice({ severity: 'warning', message: 'Keine Verbindung zum Server. Änderungen wurden nur lokal gespeichert.' });
      } else {
        setError(requestError.response?.data?.error || 'Stationsbogen konnte nicht gespeichert werden.');
      }
      throw requestError;
    } finally {
      setBusy(false);
    }
  };

  const handleFinalizeStation = async () => {
    if (isStationReadOnly) {
      setError('Dieser Stationsbogen ist finalisiert und muss erst über "Bogen entsperren" freigegeben werden.');
      return;
    }

    if (isOffline) {
      setError('Finalisieren ist nur mit Verbindung zum Server möglich.');
      return;
    }

    try {
      await handleSaveStation();
      setBusy(true);
      await client.post(`/api/assessments/events/${selectedEventId}/candidates/${selectedCandidateId}/stations/${selectedStationCode}/finalize`);
      await refreshAfterStationChange();
      setSuccess('Stationsbogen finalisiert.');
    } catch (requestError) {
      if (!requestError.response) {
        setError('Stationsbogen konnte nicht finalisiert werden.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUnlockStation = async () => {
    const trimmedReason = String(unlockReason || '').trim();
    if (!trimmedReason) {
      setError('Zum Entsperren des Stationsbogens ist eine Begründung erforderlich.');
      setSuccess('');
      return;
    }

    if (isOffline) {
      setError('Entsperren ist nur mit Verbindung zum Server möglich.');
      setSuccess('');
      return;
    }

    try {
      setBusy(true);
      setError('');
      setSuccess('');
      await client.post(
        `/api/assessments/events/${selectedEventId}/candidates/${selectedCandidateId}/stations/${selectedStationCode}/unlock`,
        { reason: trimmedReason }
      );
      await refreshAfterStationChange();
      setSuccess('Stationsbogen entsperrt.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Stationsbogen konnte nicht entsperrt werden.');
    } finally {
      setBusy(false);
    }
  };

  const handlePrepareOfflineEvent = async () => {
    if (!selectedEventId) {
      return;
    }

    if (!ASSESSMENT_OFFLINE_MODE_ENABLED) {
      setError('Offlinebetrieb für das Eignungsverfahren ist derzeit deaktiviert, solange die Zeitnahme serverseitig geführt wird.');
      return;
    }

    try {
      setBusy(true);
      setError('');
      setSuccess('');
      const response = await client.get(`/api/assessments/events/${selectedEventId}/offline-package`);
      writeOfflinePackage(selectedEventId, response.data);
      setOfflinePreparedEvents(listPreparedOfflineEvents());
      setDraftNotice({ severity: 'success', message: 'Verfahren für Offlinebetrieb vorbereitet.' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Offline-Paket konnte nicht vorbereitet werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleSyncQueuedChanges = async () => {
    if (!ASSESSMENT_OFFLINE_MODE_ENABLED) {
      setError('Offline-Synchronisation für das Eignungsverfahren ist derzeit deaktiviert.');
      return;
    }

    if (isOffline) {
      setError('Keine Verbindung zum Server. Synchronisation ist derzeit nicht möglich.');
      return;
    }

    const pendingItems = readSyncQueue();
    if (!pendingItems.length) {
      setDraftNotice({ severity: 'info', message: 'Es liegen keine lokalen Änderungen zur Synchronisation vor.' });
      return;
    }

    let syncedCount = 0;

    try {
      setBusy(true);
      setError('');
      setSuccess('');
      setDraftNotice(null);

      const response = await client.post('/api/assessments/stations/sync', {
        changes: pendingItems.map((item) => ({
          eventId: item.eventId,
          candidateId: item.candidateId,
          stationCode: item.stationCode,
          rawPayload: item.rawPayload || {},
          remarks: item.remarks || '',
          updatedAt: item.updatedAt || null,
        })),
      });

      const resultItems = Array.isArray(response.data?.results) ? response.data.results : [];
      const remainingItems = [];

      for (const item of pendingItems) {
        const result = resultItems.find((resultItem) => (
          String(resultItem.eventId) === String(item.eventId)
          && String(resultItem.candidateId) === String(item.candidateId)
          && String(resultItem.stationCode) === String(item.stationCode)
        ));

        if (result?.status === 'synced') {
          syncedCount += 1;
          removeAssessmentDraft(buildAssessmentDraftKey(item.eventId, item.candidateId, item.stationCode));
          continue;
        }

        remainingItems.push({
          ...item,
          syncStatus: 'conflict',
          lastError: result?.error || 'Lokale Änderung konnte nicht synchronisiert werden.',
          lastErrorCode: result?.code || 'sync_conflict',
          lastAttemptAt: new Date().toISOString(),
        });
      }

      writeSyncQueue(remainingItems);
      setSyncQueueItems(remainingItems);

      if (selectedEventId) {
        await loadEventBundle(selectedEventId);
        if (selectedCandidateId && selectedStationCode) {
          await loadStation(selectedEventId, selectedCandidateId, selectedStationCode);
        }
      }

      if (remainingItems.length > 0) {
        setDraftNotice({ severity: 'warning', message: `${syncedCount} lokale Änderungen synchronisiert. ${remainingItems.length} Änderungen konnten nicht übernommen werden.` });
      } else {
        setDraftNotice({ severity: 'success', message: 'Synchronisation abgeschlossen.' });
      }
    } catch (requestError) {
      if (isNetworkLikeError(requestError)) {
        setDraftNotice({ severity: 'warning', message: 'Synchronisation unterbrochen. Änderungen warten weiter auf Synchronisation.' });
      } else {
        setError(requestError.response?.data?.error || 'Synchronisation konnte nicht abgeschlossen werden.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRestoreDraft = () => {
    if (!pendingDraftRecovery?.draft?.formData) {
      return;
    }

    setStationForm(pendingDraftRecovery.draft.formData);
    if (stationDetail?.timerSession) {
      restorePersistentTimer(stationDetail.timerSession);
    } else {
      resetTimerState();
    }
    setDraftNotice({ severity: 'success', message: 'Lokaler Entwurf wiederhergestellt.' });
    setPendingDraftRecovery(null);
    setDraftSyncEnabled(true);
  };

  const handleDiscardDraft = () => {
    if (pendingDraftRecovery?.key) {
      removeAssessmentDraft(pendingDraftRecovery.key);
    }

    setDraftNotice({ severity: 'success', message: 'Lokaler Entwurf verworfen.' });
    setPendingDraftRecovery(null);
    setDraftSyncEnabled(true);
  };

  const syncStationTimerSession = useCallback(async (nextForm, nextTimerState) => {
    if (!selectedEventId || !selectedCandidateId || !selectedStationCode) {
      return;
    }

    if (isOffline) {
      throw new Error('assessment_offline_disabled');
    }

    const response = await updateAssessmentStationTimerSession(
      selectedEventId,
      selectedCandidateId,
      selectedStationCode,
      {
        rawPayload: buildPayloadForSave(nextForm),
        timerSession: buildAssessmentPersistentTimer(nextTimerState),
      },
    );

    setStationDetail(response.data?.station || null);
    setStationForm(nextForm);
    serverFormSnapshotRef.current = stringifyDraftForm(nextForm);
    removeAssessmentDraft(currentDraftKey);
    setPendingDraftRecovery(null);
    setDraftSyncEnabled(true);

    const persistedTimer = readAssessmentPersistentTimer(response.data?.station?.timerSession || buildAssessmentPersistentTimer(nextTimerState));
    if (persistedTimer) {
      restorePersistentTimer(persistedTimer);
      return;
    }

    resetTimerState();
  }, [buildPayloadForSave, currentDraftKey, isOffline, resetTimerState, restorePersistentTimer, selectedCandidateId, selectedEventId, selectedStationCode]);

  const handleTimerStart = async (path, label) => {
    if (isStationReadOnly) {
      return;
    }

    if (isOffline && !ASSESSMENT_OFFLINE_MODE_ENABLED) {
      setError('Offlinebetrieb für das Eignungsverfahren ist derzeit deaktiviert. Laufende Zeitnahmen benötigen eine Serververbindung.');
      return;
    }

    const isLadderStation = activeStationDefinition?.scoringMode === 'ladder';
    let nextForm = stationForm;

    if (isLadderStation && path === 'run2.timeMs' && timerRunning && timerTargetPath === 'pauseBetweenRunsMs') {
      nextForm = setPathValue(stationForm, 'pauseBetweenRunsMs', String(roundToSecondMs(liveElapsedMs)));
    }

    const baseValue = Number(getPathValue(nextForm, path) || 0);
    setError('');

    try {
      await syncStationTimerSession(nextForm, {
        running: true,
        targetPath: path,
        targetLabel: label,
        startReference: Date.now() - baseValue,
      });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Timer konnte nicht gestartet werden.');
    }
  };

  const handleTimerStop = async () => {
    if (isStationReadOnly) {
      return;
    }

    if (!timerTargetPath) {
      return;
    }

    const nextForm = setPathValue(stationForm, timerTargetPath, String(roundToSecondMs(liveElapsedMs)));
    setError('');

    try {
      await syncStationTimerSession(nextForm, null);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Timer konnte nicht gestoppt werden.');
    }
  };

  const handleTimerReset = async (path) => {
    if (isStationReadOnly) {
      return;
    }

    const targetPath = path || timerTargetPath;
    if (!targetPath) {
      return;
    }

    const nextForm = setPathValue(stationForm, targetPath, '0');
    const nextTimerState = timerTargetPath === targetPath ? null : getCurrentRunningTimerState();
    setError('');

    try {
      await syncStationTimerSession(nextForm, nextTimerState);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Timer konnte nicht zurückgesetzt werden.');
    }
  };

  const openTimerResetDialog = (path, label) => {
    if (isStationReadOnly) {
      return;
    }
    setTimerResetDialog({ open: true, path, label });
  };

  const confirmTimerReset = () => {
    if (isStationReadOnly) {
      setTimerResetDialog({ open: false, path: '', label: '' });
      return;
    }

    const { path } = timerResetDialog;
    handleTimerReset(path);
    setTimerResetDialog({ open: false, path: '', label: '' });
  };

  const openManualTimeDialog = (path, label) => {
    if (isStationReadOnly) {
      return;
    }

    const currentMs = Number(getPathValue(stationForm, path) || 0);
    setManualTimeDialog({
      open: true,
      path,
      label,
      value: formatDuration(currentMs),
      error: '',
    });
  };

  const confirmManualTime = async () => {
    if (isStationReadOnly) {
      setManualTimeDialog({ open: false, path: '', label: '', value: '00:00', error: '' });
      return;
    }

    const parsedMs = parseMmSsToMs(manualTimeDialog.value);
    if (parsedMs == null) {
      setManualTimeDialog((current) => ({ ...current, error: 'Bitte Zeit als MM:SS eingeben.' }));
      return;
    }

    const nextForm = setPathValue(stationForm, manualTimeDialog.path, String(parsedMs));
    const nextTimerState = timerTargetPath === manualTimeDialog.path ? null : getCurrentRunningTimerState();

    try {
      setError('');
      await syncStationTimerSession(nextForm, nextTimerState);
      if (timerTargetPath === manualTimeDialog.path) {
        setLiveElapsedMs(parsedMs);
      }
      setManualTimeDialog({ open: false, path: '', label: '', value: '00:00', error: '' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Zeit konnte nicht gespeichert werden.');
    }
  };

  useEffect(() => {
    if (!isStationReadOnly) {
      return;
    }

    setTimerRunning(false);
    setTimerStartReference(null);
    setTimerTargetPath('');
    setTimerTargetLabel('');
    setLiveElapsedMs(0);
    setTimerResetDialog({ open: false, path: '', label: '' });
    setManualTimeDialog({ open: false, path: '', label: '', value: '00:00', error: '' });
  }, [isStationReadOnly]);

  useEffect(() => {
    if (activeStationDefinition?.scoringMode !== 'ladder') {
      return;
    }
    if (!timerRunning || timerTargetPath !== 'pauseBetweenRunsMs') {
      return;
    }
    if (liveElapsedMs < (activeStationDefinition.pauseLimitMs || 180000)) {
      return;
    }

    const pauseMs = roundToSecondMs(liveElapsedMs);
    const nextForm = setPathValue(stationForm, 'pauseBetweenRunsMs', String(pauseMs));
    const run2Base = Number(getPathValue(nextForm, 'run2.timeMs') || 0);

    syncStationTimerSession(nextForm, {
      running: true,
      targetPath: 'run2.timeMs',
      targetLabel: 'Durchgang 2 Zeit',
      startReference: Date.now() - run2Base,
    }).catch((requestError) => {
      setError(requestError.response?.data?.error || 'Timer konnte nicht auf Durchgang 2 umgestellt werden.');
    });
  }, [
    activeStationDefinition,
    liveElapsedMs,
    syncStationTimerSession,
    stationForm,
    timerRunning,
    timerTargetPath,
  ]);

  const renderTimerField = (path, label) => {
    const isActive = timerTargetPath === path;
    const currentValue = isActive && timerRunning ? liveElapsedMs : Number(getPathValue(stationForm, path) || 0);

    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TimerIcon fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
            {isActive && timerRunning ? <Chip label="Läuft" color="warning" size="small" /> : null}
          </Stack>
          <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {formatDuration(currentValue)}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={() => handleTimerStart(path, label)} disabled={timerRunning && !isActive}>Start</Button>
            <Button variant="outlined" onClick={handleTimerStop} disabled={!timerRunning || !isActive}>Stopp</Button>
            <Button variant="outlined" color="inherit" onClick={() => openTimerResetDialog(path, label)}>Zurücksetzen</Button>
            <Button variant="outlined" onClick={() => openManualTimeDialog(path, label)}>Zeit manuell eingeben</Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderScoreSummary = () => {
    const scorePayload = stationDetail?.scorePayload;
    if (!scorePayload) {
      return null;
    }

    if (scorePayload.scoringMode === 'station6') {
      const question3PreparationPassed = station6Score.question3Preparation?.passed;
      const question4Passed = station6Score.question4?.passed ?? station6Score.question3?.passed;
      const isQuestion4Only = station6Score.station6QuestionSet === 'question4_only';
      return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {isQuestion4Only ? (
            <Chip label={`Frage 4: ${question4Passed ? 'Bestanden' : 'Nicht bestanden'}`} color={question4Passed ? 'success' : 'error'} />
          ) : (
            <>
              <Chip label={`Frage 1: ${station6Score.question1?.passed ? 'Bestanden' : 'Nicht bestanden'}`} color={station6Score.question1?.passed ? 'success' : 'error'} />
              <Chip label={`Frage 2: ${station6Score.question2?.passed ? 'Bestanden' : 'Nicht bestanden'}`} color={station6Score.question2?.passed ? 'success' : 'error'} />
              <Chip label={`Frage 3: ${question3PreparationPassed ? 'Bestanden' : 'Nicht bestanden'}`} color={question3PreparationPassed ? 'success' : 'error'} />
              <Chip label={`Frage 4: ${question4Passed ? 'Bestanden' : 'Nicht bestanden'}`} color={question4Passed ? 'success' : 'error'} />
            </>
          )}
        </Stack>
      );
    }

    if (scorePayload.scoringMode === 'knots') {
      return (
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Aktuelle Auswertung</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(scorePayload.methods || []).map((method) => (
              <Chip
                key={method.id}
                label={`${method.label}: ${method.rawPoints} Rohpunkte (${formatDuration(method.timeMs)})`}
                size="small"
              />
            ))}
            <Chip label={`Gesamt: ${scorePayload.points} / ${scorePayload.maxPoints}`} color={scorePayload.passed ? 'success' : 'warning'} size="small" />
          </Stack>
        </Stack>
      );
    }

    if (scorePayload.scoringMode === 'time_deductions') {
      return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`Zeit: ${formatDuration(scorePayload.timeMs)}`} size="small" />
          <Chip label={`Grundwert: ${scorePayload.basePoints}`} size="small" />
          <Chip label={`Abzüge: ${scorePayload.deductionTotal}`} size="small" />
          <Chip label={`Gesamt: ${scorePayload.points} / ${scorePayload.maxPoints}`} color={scorePayload.passed ? 'success' : 'warning'} size="small" />
        </Stack>
      );
    }

    if (scorePayload.scoringMode === 'ladder') {
      return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`DG 1: ${scorePayload.run1?.points || 0} Punkte`} size="small" />
          <Chip label={`DG 2: ${scorePayload.run2?.points || 0} Punkte`} size="small" />
          <Chip label={`Pause: ${formatDuration(scorePayload.pauseBetweenRunsMs || 0)}`} size="small" />
          <Chip label={`Gesamt: ${scorePayload.points} / ${scorePayload.maxPoints}`} color={scorePayload.passed ? 'success' : 'warning'} size="small" />
        </Stack>
      );
    }

    if (scorePayload.scoringMode === 'matrix') {
      return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`Zeit: ${formatDuration(scorePayload.timeMs)}`} size="small" />
          <Chip label={`Matrix: ${scorePayload.summedPoints} Punkte`} size="small" />
          <Chip label={`Gesamt: ${scorePayload.points} / ${scorePayload.maxPoints}`} color={scorePayload.passed ? 'success' : 'warning'} size="small" />
        </Stack>
      );
    }

    return null;
  };

  const renderKnotsForm = () => (
    <Stack spacing={2}>
      {(activeStationDefinition.methods || []).map((method) => (
        <Paper key={method.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{method.label}</Typography>
            {renderTimerField(`methods.${method.id}.timeMs`, `Zeit ${method.label}`)}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Allgemeine Bewertung</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant={Number(stationForm.methods?.[method.id]?.additionalMinorErrors || 0) > 0 ? 'contained' : 'outlined'}
                    color={Number(stationForm.methods?.[method.id]?.additionalMinorErrors || 0) > 0 ? 'warning' : 'inherit'}
                    onClick={() => {
                      const active = Number(stationForm.methods?.[method.id]?.additionalMinorErrors || 0) > 0;
                      updateFormPath(`methods.${method.id}.additionalMinorErrors`, active ? '0' : '1');
                    }}
                  >
                    Falsche Reihenfolge (-1)
                  </Button>
                </Stack>
              </Stack>
            </Paper>
            {(activeStationDefinition.knots || []).map((knot) => (
              <Paper key={knot.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{knot.label}</Typography>
                  {(() => {
                    const knotValues = stationForm.methods?.[method.id]?.knots?.[knot.id] || {};
                    const knotFailedActive = Boolean(knotValues.failed);
                    return (
                  <Grid container spacing={1.25}>
                    {(activeStationDefinition.deductionItems || []).map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        {(() => {
                          const isHmsBremsseilseite = knot.id === 'hms' && item.id === 'badTailLength';
                          const itemPoints = isHmsBremsseilseite ? 2 : item.points;
                          const itemLabel = isHmsBremsseilseite ? 'Bremsseilseite' : item.label;
                          return item.type === 'boolean' ? (
                          <Button
                            fullWidth
                            size="small"
                            disabled={knotFailedActive && item.id !== 'failed'}
                            variant={Boolean(knotValues[item.id]) ? 'contained' : 'outlined'}
                            color={Boolean(knotValues[item.id]) ? 'warning' : 'inherit'}
                            onClick={() => {
                              const isActive = Boolean(knotValues[item.id]);
                              if (item.id === 'failed') {
                                if (isActive) {
                                  updateFormPath(`methods.${method.id}.knots.${knot.id}.failed`, false);
                                } else {
                                  (activeStationDefinition.deductionItems || []).forEach((deductionItem) => {
                                    updateFormPath(`methods.${method.id}.knots.${knot.id}.${deductionItem.id}`, deductionItem.id === 'failed');
                                  });
                                }
                                return;
                              }

                              updateFormPath(`methods.${method.id}.knots.${knot.id}.${item.id}`, !isActive);
                            }}
                          >
                            {item.id === 'failed' ? itemLabel : `${itemLabel} (-${itemPoints})`}
                          </Button>
                        ) : (
                          <Stack spacing={0.75}>
                            <Typography variant="body2" color="text.secondary">{`${itemLabel} (-${itemPoints})`}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const value = Number(stationForm.methods?.[method.id]?.knots?.[knot.id]?.[item.id] || 0);
                                  updateFormPath(`methods.${method.id}.knots.${knot.id}.${item.id}`, String(Math.max(0, value - 1)));
                                }}
                              >
                                -1
                              </Button>
                              <Chip label={`${Number(stationForm.methods?.[method.id]?.knots?.[knot.id]?.[item.id] || 0)}x`} size="small" />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  const value = Number(stationForm.methods?.[method.id]?.knots?.[knot.id]?.[item.id] || 0);
                                  updateFormPath(`methods.${method.id}.knots.${knot.id}.${item.id}`, String(Math.min(99, value + 1)));
                                }}
                              >
                                +1
                              </Button>
                            </Stack>
                          </Stack>
                          );
                        })()}
                      </Grid>
                    ))}
                  </Grid>
                    );
                  })()}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>
      ))}
      <TextField
        label="Bemerkungen"
        multiline
        minRows={3}
        value={stationForm.remarks || ''}
        onChange={(event) => setStationForm((current) => ({ ...current, remarks: event.target.value }))}
      />
    </Stack>
  );

  const renderTimeDeductionForm = () => (
    <Stack spacing={2}>
      {renderTimerField('timeMs', 'Stationszeit')}
      {(activeStationDefinition.groups || []).map((group) => (
        <Paper key={group.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{group.label}</Typography>
            <Grid container spacing={1.25}>
              {group.items.map((item) => (
                <Grid item xs={12} sm={6} key={item.id}>
                  <Button
                    fullWidth
                    size="small"
                    variant={Boolean(stationForm.groups?.[group.id]?.[item.id]) ? 'contained' : 'outlined'}
                    color={Boolean(stationForm.groups?.[group.id]?.[item.id]) ? 'warning' : 'inherit'}
                    onClick={() => {
                      const isStation2RopePlacement = activeStationDefinition?.code === 'station_2_chain' && group.id === 'ropePlacement';
                      const isStation31ExclusiveGroup = activeStationDefinition?.code === 'station_3_1_express' && ['clamp', 'pulley', 'ropeRun'].includes(group.id);
                      const isStation32ExclusiveGroup = activeStationDefinition?.code === 'station_3_2_ratio' && group.id === 'pulley';
                      const requiresExclusiveSelection = isStation2RopePlacement || isStation31ExclusiveGroup || isStation32ExclusiveGroup;
                      const isActive = Boolean(stationForm.groups?.[group.id]?.[item.id]);
                      if (!requiresExclusiveSelection) {
                        updateFormPath(`groups.${group.id}.${item.id}`, !isActive);
                        return;
                      }

                      const nextGroupState = Object.fromEntries(group.items.map((groupItem) => [groupItem.id, false]));
                      nextGroupState[item.id] = !isActive;
                      updateFormPath(`groups.${group.id}`, nextGroupState);
                    }}
                  >
                    {`${item.label} (-${item.points})`}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Paper>
      ))}
      {(activeStationDefinition.fatalRules || []).length ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Ausschlusskriterien nach Zeitnahmenende</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(activeStationDefinition.fatalRules || []).map((rule) => {
                const selected = Boolean(stationForm[rule.id]);
                return (
                  <Button
                    key={rule.id}
                    size="small"
                    variant={selected ? 'contained' : 'outlined'}
                    color={selected ? 'error' : 'inherit'}
                    onClick={() => setStationForm((current) => ({ ...current, [rule.id]: !selected }))}
                  >
                    {rule.label}
                  </Button>
                );
              })}
            </Stack>
          </Stack>
        </Paper>
      ) : null}
      <TextField
        label="Bemerkungen"
        multiline
        minRows={3}
        value={stationForm.remarks || ''}
        onChange={(event) => setStationForm((current) => ({ ...current, remarks: event.target.value }))}
      />
    </Stack>
  );

  const renderLadderTimerField = (path, label) => {
    const isActive = timerTargetPath === path;
    const currentValue = isActive && timerRunning ? liveElapsedMs : Number(getPathValue(stationForm, path) || 0);
    const canSwitchFromPauseToRun2 = timerRunning && timerTargetPath === 'pauseBetweenRunsMs' && path === 'run2.timeMs';

    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TimerIcon fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
            {isActive && timerRunning ? <Chip label="Läuft" color="warning" size="small" /> : null}
          </Stack>
          <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {formatDuration(currentValue)}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={() => handleTimerStart(path, label)} disabled={timerRunning && !isActive && !canSwitchFromPauseToRun2}>Start</Button>
            <Button variant="outlined" onClick={handleTimerStop} disabled={!timerRunning || !isActive}>Stopp</Button>
            <Button variant="outlined" color="inherit" onClick={() => openTimerResetDialog(path, label)}>Zurücksetzen</Button>
            <Button variant="outlined" onClick={() => openManualTimeDialog(path, label)}>Zeit manuell eingeben</Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderLadderRun = (runId, title) => {
    const runState = stationForm[runId] || {};
    const climbingMode = runState.majorInsecurity ? 'major' : runState.minorInsecurity ? 'minor' : 'none';
    const pauseSelected = Number(runState.pauseCount || 0) > 0;
    const pullUpValue = runState.pullUp || '';

    const setClimbingMode = (mode) => {
      if (mode === 'none') {
        updateFormPath(`${runId}.majorInsecurity`, false);
        updateFormPath(`${runId}.minorInsecurity`, false);
        updateFormPath(`${runId}.pauseCount`, '0');
        return;
      }

      updateFormPath(`${runId}.majorInsecurity`, mode === 'major');
      updateFormPath(`${runId}.minorInsecurity`, mode === 'minor');
    };

    const togglePause = () => {
      updateFormPath(`${runId}.pauseCount`, pauseSelected ? '0' : '1');
    };

    const togglePullUp = (value) => {
      updateFormPath(`${runId}.pullUp`, pullUpValue === value ? '' : value);
    };

    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
          {renderLadderTimerField(`${runId}.timeMs`, `${title} Zeit`)}

          <Stack spacing={0.75}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Steigen</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant={climbingMode === 'major' ? 'contained' : 'outlined'}
                color={climbingMode === 'major' ? 'warning' : 'inherit'}
                onClick={() => setClimbingMode(climbingMode === 'major' ? 'none' : 'major')}
              >
                Deutliche Unsicherheit (-5)
              </Button>
              <Button
                size="small"
                variant={climbingMode === 'minor' ? 'contained' : 'outlined'}
                color={climbingMode === 'minor' ? 'warning' : 'inherit'}
                onClick={() => setClimbingMode(climbingMode === 'minor' ? 'none' : 'minor')}
              >
                Geringe Unsicherheit (-3)
              </Button>
              <Button
                size="small"
                variant={pauseSelected ? 'contained' : 'outlined'}
                color={pauseSelected ? 'warning' : 'inherit'}
                onClick={togglePause}
              >
                Pause (-1)
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={0.75}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Klimmzug</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant={pullUpValue === 'incomplete' ? 'contained' : 'outlined'}
                color={pullUpValue === 'incomplete' ? 'warning' : 'inherit'}
                onClick={() => togglePullUp('incomplete')}
              >
                Unvollständig (-1)
              </Button>
              <Button
                size="small"
                variant={pullUpValue === 'hinted' ? 'contained' : 'outlined'}
                color={pullUpValue === 'hinted' ? 'warning' : 'inherit'}
                onClick={() => togglePullUp('hinted')}
              >
                Nur angedeutet (-3)
              </Button>
              <Button
                size="small"
                variant={pullUpValue === 'notDone' ? 'contained' : 'outlined'}
                color={pullUpValue === 'notDone' ? 'warning' : 'inherit'}
                onClick={() => togglePullUp('notDone')}
              >
                Nicht gemacht (-5)
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderLadderForm = () => (
    <Stack spacing={2}>
      {renderLadderRun('run1', 'Durchgang 1')}
      {renderLadderTimerField('pauseBetweenRunsMs', 'Pause zwischen Durchgang 1 und 2')}
      {renderLadderRun('run2', 'Durchgang 2')}
      <Stack direction="row" spacing={1}>
        <Button
          variant={stationForm.twoFalls ? 'contained' : 'outlined'}
          color={stationForm.twoFalls ? 'error' : 'inherit'}
          onClick={() => setStationForm((current) => ({ ...current, twoFalls: !current.twoFalls }))}
        >
          2 mal gestürzt
        </Button>
      </Stack>
      <TextField
        label="Bemerkungen"
        multiline
        minRows={3}
        value={stationForm.remarks || ''}
        onChange={(event) => setStationForm((current) => ({ ...current, remarks: event.target.value }))}
      />
    </Stack>
  );

  const renderMatrixForm = () => (
    <Stack spacing={2}>
      {renderLadderTimerField('timeMs', 'Stationszeit')}
      <Stack spacing={1.5}>
        {(activeStationDefinition.criteria || []).map((criterion) => {
          const selectedValue = stationForm.criteria?.[criterion.id] || '';
          const options = getStationFiveCriterionOptions(criterion.id);
          return (
            <Paper key={criterion.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{criterion.label}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {options.map((option) => {
                    const selected = String(selectedValue) === String(option.points);
                    return (
                      <Button
                        key={`${criterion.id}-${option.points}`}
                        size="small"
                        variant={selected ? 'contained' : 'outlined'}
                        onClick={() => updateFormPath(`criteria.${criterion.id}`, selected ? '' : String(option.points))}
                      >
                        {`${option.label} (+${option.points})`}
                      </Button>
                    );
                  })}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
        {activeStationDefinition.requiresSuitabilityDecision ? (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Geeignet für Arbeiten in exponierten Lange
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant={stationForm.suitableForExposedWork === true ? 'contained' : 'outlined'}
                  onClick={() => setStationForm((current) => ({ ...current, suitableForExposedWork: true }))}
                >
                  Ja
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant={stationForm.suitableForExposedWork === false ? 'contained' : 'outlined'}
                  onClick={() => setStationForm((current) => ({ ...current, suitableForExposedWork: false }))}
                >
                  Nein
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ) : null}
      </Stack>
      <TextField
        label="Bemerkungen"
        multiline
        minRows={3}
        value={stationForm.remarks || ''}
        onChange={(event) => setStationForm((current) => ({ ...current, remarks: event.target.value }))}
      />
    </Stack>
  );

  const selectedScenario = stationSixDefinition?.question3?.scenarios?.find(
    (scenario) => scenario.id === stationForm.question4ScenarioId
  ) || stationSixDefinition?.question3?.scenarios?.[0];
  const question2RequiredOptionId = stationSixDefinition?.question2?.requiredOptionId || stationSixDefinition?.question2?.requiredOption?.id;
  const question2RequiredOptionSelected = question2RequiredOptionId
    ? Boolean(stationForm.question2CorePointMentioned)
    : true;
  const question3RequiredOptionId = stationSixDefinition?.question3Preparation?.requiredOptionId;
  const question3RequiredOptionSelected = question3RequiredOptionId
    ? (stationForm.question3PreparationKeywords || []).includes(question3RequiredOptionId)
    : true;

  const renderStationSixForm = () => (
    <Stack spacing={2}>
      {!isStation6Question4Only ? (
      <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Frage 1: Wie sieht die Tätigkeit in der Höhenrettung nach dem absolvierten Grundlehrgang SRHT aus? Welche Erwartungshaltungshaltung wird an die Höhenretterin / an den Höhenretter nach dem GAL SRHT durch die Führung / Dritte gestellt?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Markierbare Stichworte. Bestanden ab {stationSixDefinition.question1.threshold} von {stationSixDefinition.question1.total} Nennungen.
          </Typography>
          {stationSixDefinition.question1.groups.map((group) => (
            <Box key={group.title}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{group.title}</Typography>
              <Grid container spacing={0.5}>
                {group.options.map((option) => (
                  <Grid item xs={12} sm={6} key={option.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(stationForm.question1Keywords || []).includes(option.id)}
                          onChange={() => toggleQuestionKeyword('question1Keywords', option.id)}
                        />
                      }
                      label={option.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
          <Chip
            label={`${(stationForm.question1Keywords || []).length} / ${stationSixDefinition.question1.threshold} für Bestehen`}
            color={(stationForm.question1Keywords || []).length >= stationSixDefinition.question1.threshold ? 'success' : 'warning'}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Frage 2: Mit welcher Motivation hast Du dich bei der Höhenrettung beworben?
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(stationForm.question2CorePointMentioned)}
                onChange={(event) => setStationForm((current) => ({ ...current, question2CorePointMentioned: event.target.checked }))}
              />
            }
            label={stationSixDefinition.question2?.requiredOption?.label || 'Interesse an der Aufgabe selbst wurde genannt'}
          />
          <Typography variant="body2" color="text.secondary">
            Pflichtkriterium: {(stationSixDefinition.question2?.requiredOption?.label || 'muss markiert werden')}
          </Typography>
          <Chip
            label={question2RequiredOptionSelected ? 'Pflichtkriterium erfüllt' : 'Pflichtkriterium fehlt'}
            color={question2RequiredOptionSelected ? 'success' : 'warning'}
          />
          <TextField
            label={stationSixDefinition.question2?.notesLabel || 'Weitere durch den Bewerber genannte Punkte'}
            multiline
            minRows={3}
            value={stationForm.question2AdditionalNotes || ''}
            onChange={(event) => setStationForm((current) => ({ ...current, question2AdditionalNotes: event.target.value }))}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Frage 3: {stationSixDefinition.question3Preparation?.introText}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pflichtkriterium: {(stationSixDefinition.question3Preparation?.options || []).find((option) => option.id === question3RequiredOptionId)?.label || 'muss markiert werden'}
          </Typography>
          <Grid container spacing={0.5}>
            {(stationSixDefinition.question3Preparation?.options || []).map((option) => (
              <Grid item xs={12} md={6} key={option.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(stationForm.question3PreparationKeywords || []).includes(option.id)}
                      onChange={() => toggleQuestionKeyword('question3PreparationKeywords', option.id)}
                    />
                  }
                  label={option.id === question3RequiredOptionId ? `${option.label} (Pflicht)` : option.label}
                />
              </Grid>
            ))}
          </Grid>
          <Chip
            label={question3RequiredOptionSelected ? 'Pflichtkriterium erfüllt' : 'Pflichtkriterium fehlt'}
            color={question3RequiredOptionSelected ? 'success' : 'warning'}
          />
          <TextField
            label={stationSixDefinition.question3Preparation?.notesLabel || 'Weitere durch den Bewerber genannte Punkte'}
            multiline
            minRows={3}
            value={stationForm.question3PreparationAdditionalNotes || ''}
            onChange={(event) => setStationForm((current) => ({ ...current, question3PreparationAdditionalNotes: event.target.value }))}
          />
        </Stack>
      </Paper>
      </>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {isStation6Question4Only
              ? `Frage 4 (einzige Pflichtfrage): ${selectedScenario?.prompt || ''}`
              : `Frage 4: ${selectedScenario?.prompt || ''}`}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="station6-question4-label">Variante</InputLabel>
            <Select
              labelId="station6-question4-label"
              label="Variante"
              value={stationForm.question4ScenarioId || 'A'}
              onChange={(event) => setStationForm((current) => ({ ...current, question4ScenarioId: event.target.value, question4Keywords: [] }))}
            >
              {stationSixDefinition.question3.scenarios.map((scenario) => (
                <MenuItem key={scenario.id} value={scenario.id}>{scenario.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedScenario ? (
            <>
              <Grid container spacing={0.5}>
                {selectedScenario.options.map((option) => (
                  <Grid item xs={12} md={6} key={option.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(stationForm.question4Keywords || []).includes(option.id)}
                          onChange={() => toggleQuestionKeyword('question4Keywords', option.id)}
                        />
                      }
                      label={option.label}
                    />
                  </Grid>
                ))}
              </Grid>
              <Chip
                label={`${(stationForm.question4Keywords || []).length} / ${Math.ceil(selectedScenario.options.length * stationSixDefinition.question3.passRatio)} für Bestehen`}
                color={(stationForm.question4Keywords || []).length >= Math.ceil(selectedScenario.options.length * stationSixDefinition.question3.passRatio) ? 'success' : 'warning'}
              />
            </>
          ) : null}
        </Stack>
      </Paper>

      <TextField
        label="Bemerkungen"
        multiline
        minRows={3}
        value={stationForm.remarks || ''}
        onChange={(event) => setStationForm((current) => ({ ...current, remarks: event.target.value }))}
      />
    </Stack>
  );

  const renderStationForm = () => {
    if (!activeStationDefinition) {
      return null;
    }

    if (activeStationDefinition.scoringMode === 'station6') {
      return renderStationSixForm();
    }
    if (activeStationDefinition.scoringMode === 'knots') {
      return renderKnotsForm();
    }
    if (activeStationDefinition.scoringMode === 'time_deductions') {
      return renderTimeDeductionForm();
    }
    if (activeStationDefinition.scoringMode === 'ladder') {
      return renderLadderForm();
    }
    if (activeStationDefinition.scoringMode === 'matrix') {
      return renderMatrixForm();
    }
    return null;
  };

  const handleOpenMyStationDialog = (entry, station) => {
    const stationResult = (entry.stationResults || []).find((item) => item.station_code === station.code) || null;
    if (!stationResult || stationResult.status !== 'final') {
      return;
    }

    setMyStationDialog({
      open: true,
      eventLabel: entry.event?.label || 'Eignungsverfahren',
      stationTitle: station.title || station.shortTitle || station.code,
      stationResult,
    });
  };

  const candidateIndex = candidates.findIndex((candidate) => String(candidate.eventCandidateId) === String(selectedCandidateId));
  const closeSummary = useMemo(() => ({
    totalCandidates: candidates.length,
    completedCandidates: candidates.filter((candidate) => Number(candidate.openStationCount || 0) === 0).length,
    qualifiedCandidates: candidates.filter((candidate) => candidate.qualified === true).length,
    openCandidates: candidates.filter((candidate) => Number(candidate.openStationCount || 0) > 0).length,
  }), [candidates]);
  const deletePhrase = 'LOESCHEN';
  const deletePhraseMatches = deleteConfirmationInput.trim().toUpperCase() === deletePhrase;

  if (loading) {
    return (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Eignungsverfahren
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}
        {draftNotice ? <Alert severity={draftNotice.severity} onClose={() => setDraftNotice(null)}>{draftNotice.message}</Alert> : null}
        {isOffline && isEditor ? (
          <Alert severity="warning">
            {ASSESSMENT_OFFLINE_MODE_ENABLED
              ? 'Offline-Modus aktiv. Änderungen werden lokal gespeichert.'
              : 'Offlinebetrieb für das Eignungsverfahren ist derzeit deaktiviert. Für Zeitnahme und Stationsbearbeitung ist eine Serververbindung erforderlich.'}
          </Alert>
        ) : null}
        {isEditor && selectedEventId && ASSESSMENT_OFFLINE_MODE_ENABLED ? (
          <Alert
            severity={isCurrentEventPreparedOffline ? 'success' : 'info'}
            action={(
              <Button color="inherit" size="small" onClick={handlePrepareOfflineEvent} disabled={busy || isOffline}>
                Für Offlinebetrieb vorbereiten
              </Button>
            )}
          >
            {isCurrentEventPreparedOffline
              ? `Dieses Verfahren ist für den Offlinebetrieb vorbereitet${formatDraftTimestamp(currentPreparedOfflineEvent?.cachedAt) ? ` (${formatDraftTimestamp(currentPreparedOfflineEvent?.cachedAt)})` : ''}.`
              : 'Dieses Verfahren kann für den Offlinebetrieb vorbereitet werden.'}
          </Alert>
        ) : null}
        {!ASSESSMENT_OFFLINE_MODE_ENABLED && isEditor && selectedEventId ? (
          <Alert severity="info">
            Offlinepakete und lokale Synchronisation sind vorübergehend abgeschaltet, weil laufende Timer serverseitig und geräteübergreifend fortgeführt werden.
          </Alert>
        ) : null}
        {ASSESSMENT_OFFLINE_MODE_ENABLED && offlinePreparedEvents.length > 0 ? (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Offline vorbereitete Verfahren</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {offlinePreparedEvents.map((item) => (
                  <Chip
                    key={`offline-event-${item.eventId}`}
                    label={`${item.label}${formatDraftTimestamp(item.cachedAt) ? ` · ${formatDraftTimestamp(item.cachedAt)}` : ''}`}
                    size="small"
                    color={item.eventId === String(selectedEventId || '') ? 'success' : 'default'}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        ) : null}
        {ASSESSMENT_OFFLINE_MODE_ENABLED && syncQueueCount > 0 ? (
          <Alert
            severity="warning"
            action={(
              <Button color="inherit" size="small" onClick={handleSyncQueuedChanges} disabled={busy || isOffline}>
                Jetzt synchronisieren
              </Button>
            )}
          >
            {`${syncQueueCount} lokale Änderungen warten auf Synchronisation.`}
          </Alert>
        ) : null}
        {!ASSESSMENT_OFFLINE_MODE_ENABLED && syncQueueItems.length > 0 ? (
          <Alert severity="info">
            {`${syncQueueItems.length} ältere lokale Offline-Änderungen sind noch gespeichert. Neue Offline-Synchronisation ist derzeit deaktiviert.`}
          </Alert>
        ) : null}
        {ASSESSMENT_OFFLINE_MODE_ENABLED && syncQueueItems.length > 0 ? (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Lokale Änderungen</Typography>
              {syncQueueItems.map((item) => (
                <Stack
                  key={`sync-item-${item.eventId}-${item.candidateId}-${item.stationCode}`}
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatSyncQueueItemLabel(item)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.eventLabel || 'Eignungsverfahren'}
                    </Typography>
                    {item.lastError ? (
                      <Typography variant="caption" display="block" color={item.syncStatus === 'conflict' ? 'error.main' : 'text.secondary'}>
                        {formatSyncIssueMessage(item)}
                      </Typography>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color={item.syncStatus === 'conflict' ? 'error' : 'warning'}
                      label={item.syncStatus === 'conflict' ? 'Konflikt' : 'Wartet auf Synchronisation'}
                    />
                    {item.updatedAt ? <Chip size="small" label={formatDraftTimestamp(item.updatedAt)} /> : null}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Paper>
        ) : null}
        {timerRunning ? <Alert severity="info">Stoppuhr aktiv: {timerTargetLabel} ({formatDuration(liveElapsedMs)})</Alert> : null}

        <CollapsibleModuleHeaderCard
          storageKey={ASSESSMENT_HEADER_COLLAPSE_STORAGE_KEY}
          summary={(
            <FormControl fullWidth size="small" disabled={events.length === 0}>
              <InputLabel id="assessment-header-event-label">Verfahren</InputLabel>
              <Select
                labelId="assessment-header-event-label"
                label="Verfahren"
                value={selectedEventId}
                onChange={(event) => setSelectedEventId(String(event.target.value))}
              >
                {events.map((event) => (
                  <MenuItem key={event.id} value={String(event.id)}>
                    {event.label} ({event.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        >
          {selectedEventId && eventDetail ? (
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Datum: ${formatDisplayDate(eventDetail.assessment_date)}`} />
                <Chip label={`Bewerber: ${eventDetail.candidate_count || 0}`} color="secondary" />
                <Chip label={`Fertig: ${finalResults.length}`} color="primary" />
                <Chip label={getAssessmentEventStatusLabel(eventDetail.status)} color={getAssessmentEventStatusChipColor(eventDetail.status)} />
              </Stack>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                {canSeeLiveResults ? (
                  <Stack spacing={0.75}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                      <TextField
                        select
                        size="small"
                        label="PDF-Inhalt"
                        value={selectedAssessmentPdfVariant}
                        onChange={(event) => setSelectedAssessmentPdfVariant(String(event.target.value))}
                        sx={{ minWidth: { xs: '100%', sm: 320 } }}
                      >
                        {ASSESSMENT_PDF_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                      {selectedAssessmentPdfOption.requiresParticipantSelection ? (
                        <TextField
                          select
                          size="small"
                          label="Teilnehmer"
                          value={selectedAssessmentPdfCandidateIds}
                          onChange={(event) => {
                            const nextValue = Array.isArray(event.target.value)
                              ? event.target.value
                              : String(event.target.value || '').split(',').filter(Boolean);
                            setSelectedAssessmentPdfCandidateIds(nextValue.map((value) => String(value)));
                          }}
                          sx={{ minWidth: { xs: '100%', sm: 320 } }}
                          SelectProps={{
                            multiple: true,
                            displayEmpty: true,
                            renderValue: (selected) => {
                              if (!selected.length) {
                                return 'Auswählen';
                              }

                              return selected
                                .map((value) => assessmentPdfCandidateNameById.get(String(value)) || value)
                                .join(', ');
                            },
                          }}
                        >
                          {(candidates || []).map((candidate) => {
                            const candidateId = String(candidate.eventCandidateId);
                            return (
                              <MenuItem key={candidateId} value={candidateId}>
                                <Checkbox checked={selectedAssessmentPdfCandidateIds.includes(candidateId)} />
                                {candidate.fullName || '-'}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      ) : null}
                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleExportEventPdf}
                        disabled={busy || exportingEventPdf || !selectedEventId}
                      >
                        {exportingEventPdf ? 'Erstellt PDF...' : 'PDF exportieren'}
                      </Button>
                    </Stack>
                    {selectedAssessmentPdfOption.singlePdfPerParticipant ? (
                      <Typography variant="caption" color="text.secondary">
                        Mehrere ausgewählte Teilnehmer werden als einzelne PDFs nacheinander heruntergeladen.
                      </Typography>
                    ) : null}
                  </Stack>
                ) : null}
                {showManageTab ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                    {eventDetail.status !== 'active' ? (
                      <Button variant="outlined" color="info" onClick={handleActivateEvent} disabled={busy || !selectedEventId}>
                        Verfahren aktiv setzen
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<VerifiedIcon />}
                        onClick={() => setCloseDialogOpen(true)}
                        disabled={busy}
                      >
                        Verfahren abschließen
                      </Button>
                    )}
                    {(eventDetail.status === 'draft' || eventDetail.status === 'closed') ? (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => {
                          setDeleteConfirmationInput('');
                          setDeleteDialogOpen(true);
                        }}
                        disabled={busy}
                      >
                        Verfahren löschen
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          ) : (
            <Alert severity="info">
              {events.length === 0 ? 'Noch keine Verfahren vorhanden.' : 'Noch kein Verfahren ausgewählt.'}
            </Alert>
          )}
        </CollapsibleModuleHeaderCard>

        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {showBoardTab ? <Tab value="board" label="Prüfbögen" /> : null}
          {showLiveTab ? <Tab value="live" label="Live-Auswertung" /> : null}
          {showFinalTab ? <Tab value="final" label="Abschlussauswertung" /> : null}
          {showStationsTab ? <Tab value="stations" label="Stationsbeschreibung" /> : null}
          {showMineTab ? <Tab value="mine" label="Meine Ergebnisse" /> : null}
          {showManageTab ? <Tab value="manage" label="Verwaltung" /> : null}
        </Tabs>

        {activeTab === 'manage' && showManageTab ? (
          <Stack spacing={2}>
            <Card>
              <CardContent>
                <Stack spacing={2.5}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Stammdaten und Verfahrensaufbau
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <TextField label="Bezeichnung" value={eventForm.label} onChange={(event) => setEventForm((current) => ({ ...current, label: event.target.value }))} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField label="Jahr" type="number" value={eventForm.year} onChange={(event) => setEventForm((current) => ({ ...current, year: event.target.value }))} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <DateInputField label="Datum" value={eventForm.assessmentDate} onChange={(value) => setEventForm((current) => ({ ...current, assessmentDate: value }))} fullWidth helperText="Z. B. 14042026 oder 14.04.2026" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Notizen" multiline minRows={3} value={eventForm.notes} onChange={(event) => setEventForm((current) => ({ ...current, notes: event.target.value }))} fullWidth />
                    </Grid>
                  </Grid>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                    <Button variant="contained" onClick={handleCreateEvent} disabled={busy}>
                      Verfahren anlegen
                    </Button>
                    {selectedEventId ? (
                      <Button variant="outlined" onClick={handleUpdateEvent} disabled={busy}>
                        Stammdaten aktualisieren
                      </Button>
                    ) : null}
                  </Stack>

                  <Alert severity="info">
                    Auswahl, PDF-Export und Statusaktionen liegen in der Kopfzeile. Hier bleiben nur Stammdatenpflege und Verfahrensaufbau.
                  </Alert>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Beteiligte zuordnen
                  </Typography>

                  <FormControl fullWidth>
                    <InputLabel id="assessment-candidates-label">Nutzer</InputLabel>
                    <Select
                      labelId="assessment-candidates-label"
                      label="Nutzer"
                      multiple
                      value={selectedAssigneeUserIds}
                      onChange={(event) => setSelectedAssigneeUserIds(event.target.value)}
                      renderValue={(selected) => assignmentOptions.filter((option) => selected.includes(option.id)).map((option) => option.full_name).join(', ')}
                    >
                      {assignmentOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id} disabled={option.already_assigned}>
                          <Checkbox checked={selectedAssigneeUserIds.includes(option.id)} />
                          <ListItemText
                            primary={option.full_name}
                            secondary={option.already_assigned
                              ? `Bereits zugeordnet · ${getAssigneeRoleLabel(option.assignment_role)}`
                              : `${getAssigneeRoleLabel(option.assignment_role)}`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button variant="contained" onClick={handleAddAssignees} disabled={busy || !selectedAssigneeUserIds.length}>
                    Ausgewaehlte Nutzer hinzufuegen
                  </Button>

                  <Divider />

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%' }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bewerber</Typography>
                          {!groupedAssignees.candidates?.length ? (
                            <Typography variant="body2" color="text.secondary">Keine Bewerber zugeordnet.</Typography>
                          ) : groupedAssignees.candidates.map((candidate) => (
                            <Paper key={`assignee-candidate-${candidate.event_candidate_id}`} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                              <Stack spacing={0.4}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{candidate.full_name}</Typography>
                                <Button
                                  variant="text"
                                  color="error"
                                  size="small"
                                  disabled={busy}
                                  onClick={() => handleRemoveCandidate(candidate.event_candidate_id, candidate.full_name)}
                                >
                                  Entfernen
                                </Button>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%' }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Pruefer</Typography>
                          {!groupedAssignees.examiners?.length ? (
                            <Typography variant="body2" color="text.secondary">Keine Pruefer zugeordnet.</Typography>
                          ) : groupedAssignees.examiners.map((person) => (
                            <Paper key={`assignee-examiner-${person.id}`} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                              <Stack spacing={0.4}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{person.full_name}</Typography>
                                <Button
                                  variant="text"
                                  color="error"
                                  size="small"
                                  disabled={busy}
                                  onClick={() => handleRemoveAssignee(person.id, person.full_name, 'Pruefer')}
                                >
                                  Entfernen
                                </Button>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, height: '100%' }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Beobachter</Typography>
                          {!groupedAssignees.observers?.length ? (
                            <Typography variant="body2" color="text.secondary">Keine Beobachter zugeordnet.</Typography>
                          ) : groupedAssignees.observers.map((person) => (
                            <Paper key={`assignee-observer-${person.id}`} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                              <Stack spacing={0.4}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{person.full_name}</Typography>
                                <Button
                                  variant="text"
                                  color="error"
                                  size="small"
                                  disabled={busy}
                                  onClick={() => handleRemoveAssignee(person.id, person.full_name, 'Beobachter')}
                                >
                                  Entfernen
                                </Button>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Alert severity="info">
                    Pruefer werden als Pool ohne feste Stationsbindung eingesetzt.
                  </Alert>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}

        {activeTab === 'live' && canSeeLiveResults ? (
          <Stack spacing={2}>
            {liveResults.length === 0 ? (
              <Alert severity="info">Für dieses Verfahren liegen noch keine Live-Daten vor.</Alert>
            ) : (
              liveResults.map((item) => {
                const qualifiedChip = getQualifiedChipProps(item.qualified);
                const failedStationsLabel = getFailedStationsLabel(item);
                return (
                  <Card key={item.eventCandidateId}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Rang {item.rankPosition}: {item.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.totalPoints} / {item.maxPoints} Punkte · {item.percentScore}% · {item.passedStationCount} von 7 bestanden
                            </Typography>
                          </Box>
                          <Chip label={qualifiedChip.label} color={qualifiedChip.color} />
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {item.bestStationCode ? <Chip label={`Beste Station: ${stations.find((station) => station.code === item.bestStationCode)?.shortTitle || item.bestStationCode}`} size="small" /> : null}
                          {item.worstStationCode ? <Chip label={`Schwächste Station: ${stations.find((station) => station.code === item.worstStationCode)?.shortTitle || item.worstStationCode}`} size="small" /> : null}
                          {failedStationsLabel ? <Chip label={failedStationsLabel} size="small" color="error" /> : null}
                          <Chip label={`${item.openStationCount} offene Stationen`} size="small" color={item.openStationCount ? 'warning' : 'success'} />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Stack>
        ) : null}

        {activeTab === 'final' && canSeeLiveResults ? (
          <Stack spacing={2}>
            {finalResults.length === 0 ? (
              <Alert severity="info">Es liegen noch keine vollständig abgeschlossenen Verfahrensergebnisse vor.</Alert>
            ) : (
              finalResults.map((item) => {
                const qualifiedChip = getQualifiedChipProps(item.qualified);
                const failedStationsLabel = getFailedStationsLabel(item);
                return (
                  <Card key={`final-${item.eventCandidateId}`}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Rang {item.rankPosition}: {item.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.totalPoints} / {item.maxPoints} Punkte · {item.percentScore}% · {item.passedStationCount} von 7 bestanden
                            </Typography>
                          </Box>
                          <Chip label={qualifiedChip.label} color={qualifiedChip.color} />
                        </Stack>
                        <Grid container spacing={1}>
                          {item.stations.map((station) => (
                            <Grid item xs={12} sm={6} md={4} key={`${item.eventCandidateId}-${station.code}`}>
                              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{station.shortTitle}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {station.maxPoints > 0 ? `${station.points} / ${station.maxPoints} Punkte` : station.passed ? 'Bestanden' : 'Nicht bestanden'}
                                </Typography>
                                {station.failedReasonCode ? (
                                  <Typography variant="caption" color="error.main">{formatFailureReason(station.failedReasonCode)}</Typography>
                                ) : null}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {item.bestStationCode ? <Chip label={`Beste Station: ${stations.find((station) => station.code === item.bestStationCode)?.shortTitle || item.bestStationCode}`} size="small" /> : null}
                          {item.worstStationCode ? <Chip label={`Schwächste Station: ${stations.find((station) => station.code === item.worstStationCode)?.shortTitle || item.worstStationCode}`} size="small" /> : null}
                          {failedStationsLabel ? <Chip label={failedStationsLabel} size="small" color="error" /> : null}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Stack>
        ) : null}

        {activeTab === 'stations' && showStationsTab ? (
          <Stack spacing={2}>
            {stations.length === 0 ? (
              <Alert severity="info">Stationsbeschreibungen werden geladen.</Alert>
            ) : stations.map((station) => (
              <Card key={`station-description-${station.code}`}>
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {station.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {station.description || 'Keine Beschreibung hinterlegt.'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : null}

        {activeTab === 'mine' && showMineTab ? (
          <Stack spacing={2}>
            {myResults.length === 0 ? (
              <Alert severity="info">Es liegen noch keine persönlichen Ergebnisse vor.</Alert>
            ) : myResults.map((entry) => {
              const qualifiedChip = getQualifiedChipProps(entry.summary.qualified);
              return (
                <Card key={`${entry.event.id}-${entry.summary.eventCandidateId}`}>
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {entry.event.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {entry.summary.totalPoints} / {entry.summary.maxPoints} Punkte
                          </Typography>
                        </Box>
                        <Chip label={qualifiedChip.label} color={qualifiedChip.color} />
                      </Stack>
                      <Grid container spacing={1.5}>
                        {entry.summary.stations.map((station) => {
                          const chip = getStationStatusChipProps(station.status);
                          const canOpenDetail = station.status === 'final';
                          return (
                            <Grid item xs={12} sm={6} lg={4} key={station.code}>
                              <Paper
                                variant="outlined"
                                onClick={() => canOpenDetail && handleOpenMyStationDialog(entry, station)}
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  cursor: canOpenDetail ? 'pointer' : 'default',
                                  transition: 'box-shadow 120ms ease',
                                  '&:hover': canOpenDetail ? { boxShadow: 2 } : undefined,
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{station.shortTitle}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {station.maxPoints > 0 ? `${station.points} / ${station.maxPoints} Punkte` : station.passed === true ? 'Bestanden' : station.passed === false ? 'Nicht bestanden' : 'Noch offen'}
                                </Typography>
                                {station.failedReasonCode ? (
                                  <Typography variant="caption" color="error.main">{formatFailureReason(station.failedReasonCode)}</Typography>
                                ) : null}
                                {chip ? <Chip label={chip.label} color={chip.color} size="small" sx={{ mt: 1 }} /> : null}
                                {!canOpenDetail ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                    Detailansicht wird nach Finalisierung freigeschaltet.
                                  </Typography>
                                ) : null}
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : null}

        {activeTab === 'board' && isEditor ? (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <Card sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Bewerberliste
                    </Typography>
                    <FormControl fullWidth sx={{ display: { xs: 'block', lg: 'none' } }}>
                      <InputLabel id="assessment-mobile-candidate-label">Prüfling</InputLabel>
                      <Select
                        labelId="assessment-mobile-candidate-label"
                        label="Prüfling"
                        value={selectedCandidateId}
                        onChange={(event) => setSelectedCandidateId(String(event.target.value))}
                      >
                        {candidates.map((candidate) => (
                          <MenuItem key={candidate.eventCandidateId} value={String(candidate.eventCandidateId)}>
                            {candidate.fullName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {candidates.length === 0 ? (
                      <Alert severity="info">Diesem Verfahren sind noch keine Bewerber zugeordnet.</Alert>
                    ) : (
                      <List sx={{ p: 0 }}>
                        {candidates.map((candidate) => {
                          const qualifiedChip = getQualifiedChipProps(candidate.qualified);
                          return (
                            <ListItemButton
                              key={candidate.eventCandidateId}
                              selected={String(candidate.eventCandidateId) === String(selectedCandidateId)}
                              onClick={() => setSelectedCandidateId(String(candidate.eventCandidateId))}
                              sx={{ borderRadius: 2, mb: 0.75, alignItems: 'flex-start', display: { xs: 'none', lg: 'flex' } }}
                            >
                              <ListItemText
                                primary={candidate.fullName}
                                secondary={`${candidate.totalPoints} / ${candidate.maxPoints} Punkte · ${candidate.openStationCount} offen`}
                              />
                              <Stack spacing={0.75} alignItems="flex-end">
                                <Chip label={qualifiedChip.label} color={qualifiedChip.color} size="small" />
                                <Chip label={`${candidate.openStationCount} offen`} color={getProgressTone(candidate.openStationCount)} size="small" variant="outlined" />
                              </Stack>
                            </ListItemButton>
                          );
                        })}
                      </List>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={8}>
              {!activeCandidate ? (
                <Alert severity="info">Bitte wählen Sie einen Bewerber aus.</Alert>
              ) : (
                <Stack spacing={2}>
                  <Card>
                    <CardContent>
                      <Stack spacing={1.75}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                              {activeCandidate.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {activeCandidate.totalPoints} / {activeCandidate.maxPoints} Punkte · {activeCandidate.passedStationCount} von 7 bestanden · {activeCandidate.openStationCount} offen
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              startIcon={<ArrowBackIcon />}
                              onClick={() => candidateIndex > 0 && setSelectedCandidateId(String(candidates[candidateIndex - 1].eventCandidateId))}
                              disabled={candidateIndex <= 0}
                            >
                              Vorheriger
                            </Button>
                            <Button
                              variant="outlined"
                              endIcon={<ArrowForwardIcon />}
                              onClick={() => candidateIndex < candidates.length - 1 && setSelectedCandidateId(String(candidates[candidateIndex + 1].eventCandidateId))}
                              disabled={candidateIndex === -1 || candidateIndex >= candidates.length - 1}
                            >
                              Nächster
                            </Button>
                          </Stack>
                        </Stack>

                        <Grid container spacing={1.25}>
                          <Grid item xs={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, height: '100%' }}>
                              <Typography variant="overline" color="text.secondary">Gesamt</Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{activeCandidate.totalPoints} / {activeCandidate.maxPoints}</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, height: '100%' }}>
                              <Typography variant="overline" color="text.secondary">Bestanden</Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{activeCandidate.passedStationCount} / 7</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, height: '100%' }}>
                              <Typography variant="overline" color="text.secondary">Offen</Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{activeCandidate.openStationCount}</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, height: '100%' }}>
                              <Typography variant="overline" color="text.secondary">Ergebnis</Typography>
                              <Chip {...getQualifiedChipProps(activeCandidate.qualified)} size="small" sx={{ mt: 0.75 }} />
                            </Paper>
                          </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {activeCandidate.bestStationCode ? <Chip label={`Beste Station: ${stations.find((station) => station.code === activeCandidate.bestStationCode)?.shortTitle || activeCandidate.bestStationCode}`} size="small" /> : null}
                          {activeCandidate.worstStationCode ? <Chip label={`Schwächste Station: ${stations.find((station) => station.code === activeCandidate.worstStationCode)?.shortTitle || activeCandidate.worstStationCode}`} size="small" /> : null}
                          {activeCandidate.failedStationCode ? <Chip label={`Kritische Station: ${stations.find((station) => station.code === activeCandidate.failedStationCode)?.shortTitle || activeCandidate.failedStationCode}`} size="small" color="error" /> : null}
                        </Stack>

                        <Grid container spacing={1.25}>
                          {activeCandidate.stations.map((station) => {
                            const chip = getStationStatusChipProps(station.status);
                            const selected = station.code === selectedStationCode;
                            return (
                              <Grid item xs={12} sm={6} md={4} key={station.code}>
                                <Card variant={selected ? 'elevation' : 'outlined'} sx={{ borderRadius: 2.5, borderColor: selected ? 'primary.main' : 'divider', bgcolor: selected ? 'rgba(25, 118, 210, 0.06)' : 'background.paper' }}>
                                  <Button
                                    fullWidth
                                    onClick={() => setSelectedStationCode(station.code)}
                                    sx={{ textAlign: 'left', justifyContent: 'space-between', p: 1.5, color: 'text.primary', alignItems: 'flex-start' }}
                                  >
                                    <Box sx={{ textAlign: 'left' }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{station.shortTitle}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {station.maxPoints > 0 ? `${station.points} / ${station.maxPoints} Punkte` : station.passed === true ? 'Bestanden' : station.passed === false ? 'Nicht bestanden' : 'Noch offen'}
                                      </Typography>
                                      {station.failedReasonCode ? (
                                        <Typography variant="caption" display="block" color="error.main">
                                          {formatFailureReason(station.failedReasonCode)}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                    {chip ? <Chip label={chip.label} size="small" color={chip.color} /> : null}
                                  </Button>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Stack>
                    </CardContent>
                  </Card>

                  {activeStationDefinition ? (
                    <Card>
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {activeStationDefinition.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {activeStationDefinition.description}
                              </Typography>
                            </Box>
                            {stationDetail ? (
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {getStationStatusChipProps(stationDetail.status) ? <Chip {...getStationStatusChipProps(stationDetail.status)} /> : null}
                                {activeStationDefinition.maxPoints > 0 ? <Chip label={`${stationDetail.points || 0} / ${activeStationDefinition.maxPoints} Punkte`} /> : null}
                                {stationDetail.passed === true ? <Chip label="Bestanden" color="success" /> : null}
                                {stationDetail.passed === false ? <Chip label="Nicht bestanden" color="error" /> : null}
                              </Stack>
                            ) : null}
                          </Stack>

                          {renderScoreSummary()}
                          {isFinalizedStation ? (
                            <Alert severity="warning">
                              Dieser Stationsbogen ist finalisiert und schreibgeschuetzt. Aenderungen sind erst nach Klick auf "Bogen entsperren" moeglich.
                            </Alert>
                          ) : null}
                          {pendingDraftRecovery ? (
                            <Alert
                              severity="info"
                              action={(
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                  <Button color="inherit" size="small" onClick={handleRestoreDraft}>Entwurf wiederherstellen</Button>
                                  <Button color="inherit" size="small" onClick={handleDiscardDraft}>Entwurf verwerfen</Button>
                                </Stack>
                              )}
                            >
                              {`Lokaler Entwurf gefunden${formatDraftTimestamp(pendingDraftRecovery.draft?.updatedAt) ? ` (${formatDraftTimestamp(pendingDraftRecovery.draft?.updatedAt)})` : ''}. Sie koennen den Entwurf wiederherstellen oder verwerfen.`}
                            </Alert>
                          ) : null}
                          <Box component="fieldset" disabled={isStationReadOnly} sx={{ border: 0, m: 0, p: 0, minInlineSize: 0 }}>
                            {renderStationForm()}
                          </Box>

                          <Divider />

                          <Stack spacing={1.5}>
                            {stationDetail?.status === 'final' ? (
                              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                <Stack spacing={1.25}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bearbeitungsschutz</Typography>
                                    <Chip label="Finalisiert" color="success" size="small" />
                                  </Stack>
                                  <TextField
                                    label="Begründung zum Entsperren"
                                    value={unlockReason}
                                    onChange={(event) => setUnlockReason(event.target.value)}
                                    placeholder="z. B. Zahlendreher oder fehlende Ergänzung"
                                    required
                                    error={!String(unlockReason || '').trim()}
                                    helperText={!String(unlockReason || '').trim() ? 'Bitte eine Begründung als Freitext eingeben.' : ' '}
                                  />
                                  <Button variant="outlined" color="warning" startIcon={<LockOpenIcon />} onClick={handleUnlockStation} disabled={busy || !String(unlockReason || '').trim()}>
                                    Bogen entsperren
                                  </Button>
                                </Stack>
                              </Paper>
                            ) : null}

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ position: { xs: 'sticky', md: 'static' }, bottom: { xs: 8, md: 'auto' }, zIndex: 2, bgcolor: { xs: 'background.paper', md: 'transparent' }, p: { xs: 1, md: 0 }, borderRadius: { xs: 2.5, md: 0 }, boxShadow: { xs: 3, md: 'none' } }}>
                              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveStation} disabled={busy || isStationReadOnly} fullWidth>
                                Speichern
                              </Button>
                              <Button variant="contained" color="success" startIcon={<VerifiedIcon />} onClick={handleFinalizeStation} disabled={busy || isStationReadOnly} fullWidth>
                                Finalisieren
                              </Button>
                            </Stack>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : null}
                </Stack>
              )}
            </Grid>
          </Grid>
        ) : null}

        <Dialog
          open={timerResetDialog.open}
          onClose={() => setTimerResetDialog({ open: false, path: '', label: '' })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Stoppuhr zurücksetzen</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
              Soll die Zeit für {timerResetDialog.label || 'diesen Timer'} wirklich auf 00:00 gesetzt werden?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTimerResetDialog({ open: false, path: '', label: '' })}>Abbrechen</Button>
            <Button variant="contained" color="error" onClick={confirmTimerReset} disabled={isStationReadOnly}>Zurücksetzen</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={manualTimeDialog.open}
          onClose={() => setManualTimeDialog({ open: false, path: '', label: '', value: '00:00', error: '' })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Zeit manuell eingeben</DialogTitle>
          <DialogContent>
            <Stack spacing={1.25} sx={{ pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {manualTimeDialog.label || 'Timer'} als MM:SS eingeben.
              </Typography>
              <TextField
                label="Zeit (MM:SS)"
                value={manualTimeDialog.value}
                onChange={(event) => setManualTimeDialog((current) => ({ ...current, value: event.target.value, error: '' }))}
                error={Boolean(manualTimeDialog.error)}
                helperText={manualTimeDialog.error || ''}
                disabled={isStationReadOnly}
                autoFocus
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualTimeDialog({ open: false, path: '', label: '', value: '00:00', error: '' })}>Abbrechen</Button>
            <Button variant="contained" onClick={confirmManualTime} disabled={isStationReadOnly}>Übernehmen</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={myStationDialog.open}
          onClose={() => setMyStationDialog({ open: false, eventLabel: '', stationTitle: '', stationResult: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{`${myStationDialog.eventLabel} - ${myStationDialog.stationTitle}`}</DialogTitle>
          <DialogContent dividers>
            {myStationDialog.stationResult ? (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {getStationStatusChipProps(myStationDialog.stationResult.status) ? <Chip {...getStationStatusChipProps(myStationDialog.stationResult.status)} size="small" /> : null}
                  {myStationDialog.stationResult.passed === true ? <Chip label="Bestanden" color="success" size="small" /> : null}
                  {myStationDialog.stationResult.passed === false ? <Chip label="Nicht bestanden" color="error" size="small" /> : null}
                  <Chip label={`${Number(myStationDialog.stationResult.points || 0)} / ${Number(myStationDialog.stationResult.max_points || 0)} Punkte`} size="small" />
                </Stack>

                {(myStationDialog.stationResult.failedReasonCode || myStationDialog.stationResult.failed_reason_code) ? (
                  <Typography variant="body2" color="error.main">
                    {formatFailureReason(myStationDialog.stationResult.failedReasonCode || myStationDialog.stationResult.failed_reason_code)}
                  </Typography>
                ) : null}

                {myStationDialog.stationResult.remarks ? (
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Bemerkungen</Typography>
                    <Typography variant="body2" color="text.secondary">{myStationDialog.stationResult.remarks}</Typography>
                  </Paper>
                ) : null}

                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Auswertung für Teilnehmende</Typography>
                  {(() => {
                    const feedbackEntries = formatMyStationFeedback(myStationDialog.stationResult);
                    const points = Number(myStationDialog.stationResult.points || 0);
                    const maxPoints = Number(myStationDialog.stationResult.max_points || 0);
                    const missedPoints = Math.max(0, maxPoints - points);
                    const groupedFeedback = feedbackEntries.reduce((accumulator, entry) => {
                      if (!accumulator[entry.category]) {
                        accumulator[entry.category] = [];
                      }
                      accumulator[entry.category].push(entry.text);
                      return accumulator;
                    }, {});
                    const categoryOrder = ['Ausschluss', 'Zeit', 'Pflichtkriterium', 'Sicherheit', 'Technik'];
                    const categoryKeys = categoryOrder.filter((category) => Array.isArray(groupedFeedback[category]) && groupedFeedback[category].length > 0);

                    return (
                      <Stack spacing={1}>
                        {maxPoints > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Nicht erreichte Punkte: {missedPoints} von {maxPoints}
                          </Typography>
                        ) : null}

                        {categoryKeys.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Keine konkreten Fehler oder Abzüge dokumentiert.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {categoryKeys.map((category) => (
                              <Paper key={`${myStationDialog.stationTitle}-${category}`} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                                <Stack spacing={0.5}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                    {category}
                                  </Typography>
                                  {groupedFeedback[category].map((line, index) => (
                                    <Typography key={`${myStationDialog.stationTitle}-${category}-${index}`} variant="body2" color="text.secondary">
                                      - {line}
                                    </Typography>
                                  ))}
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    );
                  })()}
                </Paper>
              </Stack>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMyStationDialog({ open: false, eventLabel: '', stationTitle: '', stationResult: null })}>
              Schließen
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={closeDialogOpen} onClose={() => !busy && setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Verfahren abschliessen</DialogTitle>
          <DialogContent>
            <Stack spacing={1.25} sx={{ pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Das Verfahren wird auf abgeschlossen gesetzt. Danach wird automatisch das Ergebnis-PDF erzeugt und fuer Lehrhoehenretter+, Leitung und Admin als Benachrichtigung bereitgestellt.
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack spacing={0.75}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Zusammenfassung vor Abschluss</Typography>
                  <Typography variant="body2">Bewerber gesamt: {closeSummary.totalCandidates}</Typography>
                  <Typography variant="body2">Vollstaendig abgeschlossen: {closeSummary.completedCandidates}</Typography>
                  <Typography variant="body2">Bestanden: {closeSummary.qualifiedCandidates}</Typography>
                  <Typography variant="body2" color={closeSummary.openCandidates > 0 ? 'warning.main' : 'text.secondary'}>
                    Noch offen: {closeSummary.openCandidates}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloseDialogOpen(false)} disabled={busy}>Abbrechen</Button>
            <Button onClick={handleCloseEvent} variant="contained" color="error" disabled={busy}>Verfahren abschliessen</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => !busy && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Verfahren loeschen</DialogTitle>
          <DialogContent>
            <Stack spacing={1.25} sx={{ pt: 0.5 }}>
              <Alert severity="warning">
                Diese Aktion entfernt das Verfahren inklusive Zuordnungen und Bewertungen endgueltig.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Zur Bestaetigung bitte <strong>{deletePhrase}</strong> eingeben.
              </Typography>
              <TextField
                label="Bestaetigung"
                value={deleteConfirmationInput}
                onChange={(event) => setDeleteConfirmationInput(event.target.value)}
                autoFocus
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={busy}>Abbrechen</Button>
            <Button onClick={handleDeleteEvent} variant="contained" color="error" disabled={busy || !deletePhraseMatches}>Loeschen</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
}

export default EignungsverfahrenPage;
